import type { EdgeState, EngineLocation, FeedEngine } from '../engine';
import type {
  BufferWindow,
  FeedBufferLike,
  FeedPageMergeRequest,
  FeedPageMergeResult,
  LocalPageResult
} from './buffer';
import { createSendingChatItem } from './chatItem';
import { FeedController, type FeedRequestDirection } from './controller';
import { EntityStore, hasEntityChanges, type EntityChangeSet } from './entityStore';
import { compareSortOrders } from './ids';
import { parseMessageId } from './parsePrimitives';
import { projectMessage, projectMessages } from './project';
import { createClientRequestId, optimisticItemKey, optimisticRendererRow } from './sessionOptimistic';
import { patchRendererRowReaction, patchWireReactions } from './sessionReactions';
import {
  rememberRecentMessageEvent,
  replayableRecentMessageEvents,
  type RecentMessageEvent
} from './sessionReplay';
import {
  resolveJumpTarget,
  resolveRestoreTarget,
  validatedRestoreRecord
} from './sessionRestore';
import type { ChatFeedCursors } from './store';
import type {
  ChannelId,
  ChatAuthor,
  ChatItem,
  ClientRequestId,
  EntityBundle,
  FeedApi,
  FeedAroundAnchor,
  FeedPage,
  LiveWireReaction,
  LocalOptimisticMessage,
  MessageId,
  ReactionDelta,
  ReactionDeltaAction,
  SocketUnsubscribe,
  SortOrder,
  SyncCursor,
  SyncEvent,
  SyncResult,
  WireMessage,
  WorkspaceId
} from './types';

export { resolveJumpTarget, resolveRestoreTarget } from './sessionRestore';

const DEFAULT_PAGE_SIZE = 80;
const DEFAULT_EDGE_PAGE_THRESHOLD_PX = 2400;
const APPROACHING_OLDER_PAGE_THRESHOLD_VIEWPORTS = 6;
const DEFAULT_RESTORE_SCROLL_IDLE_DEBOUNCE_MS = 250;
export const FEED_FRESHNESS_TTL_MS = 30_000;
export const FEED_FRESHNESS_RETRY_CAPS_MS = [1_000, 5_000, 30_000] as const;

export type FeedFreshnessReason =
  | 'start'
  | 'focus'
  | 'pageshow'
  | 'visible'
  | 'online'
  | 'cable-reconnect'
  | 'resume'
  | 'heartbeat'
  | 'manual-retry';

export interface FeedFreshnessScheduler {
  readonly clearTimeout: (handle: ReturnType<typeof setTimeout>) => void;
  readonly setTimeout: (handler: () => void, delayMs: number) => ReturnType<typeof setTimeout>;
}

/** Feed API plus live socket and optional demo resync controls used by a session. */
export interface FeedSessionServer extends FeedApi {
  /** Canonical oldest row used to reject stale local-cache terminal claims. */
  conversationStartSortOrder?: SortOrder;
  subscribe(cb: (event: SyncEvent) => void): SocketUnsubscribe;
  onLocalOptimistic?: (cb: (delta: { readonly message: LocalOptimisticMessage }) => void) => SocketUnsubscribe;
  onReactionDelta?: (cb: (delta: ReactionDelta) => void) => SocketUnsubscribe;
  send(clientRequestId: ClientRequestId, body: string): Promise<MessageId | undefined>;
  resync?: {
    dropAndAccumulate(): void;
    reconnect(): void;
    forceOverflow: boolean;
  };
  sourceLabel?: string;
  tailIndex?: () => number;
  messageAt?: (index: number) => WireMessage;
}

/** Saved item anchor restore record; offsetPx is measured from viewport top. */
export interface FeedAnchorRestoreRecord {
  version: 1;
  accountId: string;
  conversationId: ChannelId;
  mode?: 'anchor';
  savedAt: number;
  id: MessageId;
  sortOrder: SortOrder;
  itemKey: string;
  offsetPx: number;
}

/** Saved restore record for a live-bottom window without a specific anchor. */
export interface FeedLatestRestoreRecord {
  version: 1;
  accountId: string;
  conversationId: ChannelId;
  mode: 'latest';
  savedAt: number;
}

/** Persisted restore target; version gates future incompatible storage shapes. */
export type FeedRestoreRecord = FeedAnchorRestoreRecord | FeedLatestRestoreRecord;

/** Message jump target; sortOrder is the stable chronology anchor, id is used when already known. */
export interface FeedMessageJumpInput {
  readonly align?: 'start' | 'center' | 'end';
  readonly id?: MessageId;
  readonly sortOrder: SortOrder;
}

/** Synchronous restore storage; implementations must not throw for missing records. */
export interface FeedRestoreStorage {
  read(accountId: string, conversationId: ChannelId): FeedRestoreRecord | undefined;
  write(record: FeedRestoreRecord): void;
  remove(accountId: string, conversationId: ChannelId): void;
}

/** Return-to-present UI state, with errors carried as display-ready messages. */
export type ReturnToPresentState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string };

/** Boot latest validation state for cached latest windows awaiting network confirmation. */
export type BootLatestValidationState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string };

/** Shared idle return-to-present state object; treat as immutable. */
export const IDLE_RETURN_TO_PRESENT_STATE: ReturnToPresentState = { kind: 'idle' };
/** Shared idle boot-validation state object; treat as immutable. */
export const IDLE_BOOT_LATEST_VALIDATION_STATE: BootLatestValidationState = { kind: 'idle' };

/** Session status snapshot; strings are already formatted for demo/debug UI. */
export interface FeedSessionSnapshot {
  readonly bootLatestValidationState: BootLatestValidationState;
  readonly bufferHealthy: boolean;
  readonly conversationId: ChannelId;
  readonly freshnessError?: string;
  readonly returnToPresentState: ReturnToPresentState;
  readonly status: string;
  readonly storageWriteFailureEnabled: boolean;
  readonly unseenPresentCount: number;
}

/** Optional side channels for buffer failures and debug events. */
export interface FeedSessionCallbacks {
  /**
   * A consumed boot jump was superseded (stop or conversation switch) before
   * it could land. Callers should hand the intent back to their pending-jump
   * store so a successor boot can still fulfill it.
   */
  readonly onBootMessageJumpAbandoned?: (conversationId: ChannelId, sortOrder: SortOrder) => void;
  readonly onBootMessageJumpInstalled?: (conversationId: ChannelId, sortOrder: SortOrder) => void;
  readonly onBufferError?: (error: unknown) => void;
  readonly onDebugEvent?: (kind: string, data?: Record<string, unknown>) => void;
}

/** Session dependencies and tuning; viewerProfileId is required to compute reactedByMe. */
export interface FeedSessionOptions {
  readonly accountId: string;
  /**
   * Consumes a pending boot-time message jump for the conversation, if any.
   * When it returns a sort order, boot installs the around-target window as
   * the first paint instead of booting to latest/restore and re-centering
   * afterwards (which paints the wrong position, then visibly jumps).
   */
  readonly bootMessageJump?: (conversationId: ChannelId) => SortOrder | undefined;
  readonly callbacks?: FeedSessionCallbacks;
  readonly buffer: FeedBufferLike;
  readonly controller: FeedController;
  readonly conversationId: ChannelId;
  readonly edgePageThresholdPx?: number;
  readonly engine: FeedEngine<ChatItem>;
  readonly entityStore: EntityStore;
  readonly freshnessNow?: () => number;
  readonly freshnessRandom?: () => number;
  readonly freshnessScheduler?: FeedFreshnessScheduler;
  readonly isDocumentVisible?: () => boolean;
  readonly isOnline?: () => boolean;
  readonly pageSize?: number;
  readonly restoreScrollIdleDebounceMs?: number;
  readonly restoreStorage?: FeedRestoreStorage;
  readonly server: FeedSessionServer;
  readonly viewerProfileId: ChatAuthor['id'];
}

interface LoadedConversation {
  readonly accountId: string;
  readonly conversationId: ChannelId;
  readonly controller: FeedController;
  readonly entityStore: EntityStore;
  readonly wireById: Map<MessageId, WireMessage>;
  readonly workspaceIds: Set<WorkspaceId>;
}

interface WorkspaceEntityFanoutSignal {
  readonly accountId: string;
  readonly workspaceId: WorkspaceId;
  readonly entityRevision: number;
  readonly changes: EntityChangeSet;
}

interface ResyncApplyResult {
  readonly ackedSends: number;
  readonly diff: {
    readonly removed: number;
    readonly updated: number;
    readonly appended: number;
    readonly withheldHistoricCreates: number;
  };
  readonly entityFanoutCount: number;
  readonly eventCount: number;
}

interface InstalledFetchedWindow {
  readonly mergeResult: FeedPageMergeResult;
  readonly page: FeedPage;
  readonly projectedMessages: ChatItem[];
  readonly replayedEventCount: number;
}

type LatestDisplayPolicy = 'preserve-reader' | 'install-at-bottom';
type LatestDisplayOutcome = 'installed' | 'preserved' | 'detached';

interface ReconciledLatestSnapshot {
  readonly displayOutcome: LatestDisplayOutcome;
  readonly mergeResult: FeedPageMergeResult;
  readonly page: FeedPage;
  readonly projectedMessages: ChatItem[];
  readonly replayedEventCount: number;
}

type ReconcileLatestSnapshotResult =
  | ({ readonly kind: 'success' } & ReconciledLatestSnapshot)
  | { readonly kind: 'error'; readonly error: unknown }
  | { readonly kind: 'merge-failed' }
  | { readonly kind: 'revoked' | 'stale' };

type InstallFetchedWindowResult =
  | ({ readonly kind: 'installed' } & InstalledFetchedWindow)
  | { readonly kind: 'dropped'; readonly reason: string }
  | { readonly kind: 'error'; readonly error: unknown }
  | { readonly kind: 'merge-failed'; readonly page: FeedPage }
  | { readonly kind: 'missing-location'; readonly page: FeedPage; readonly projectedMessages: ChatItem[] }
  | { readonly kind: 'not-installed'; readonly page: FeedPage; readonly projectedMessages: ChatItem[] }
  | { readonly kind: 'revoked'; readonly page: FeedPage }
  | { readonly kind: 'skipped'; readonly reason: string }
  | { readonly kind: 'stale'; readonly page?: FeedPage };

interface InstallFetchedWindowInput {
  readonly cursors: (page: FeedPage) => ChatFeedCursors;
  readonly direction: FeedRequestDirection;
  readonly edges?: (page: FeedPage) => EdgeState;
  readonly fetch: () => Promise<FeedPage>;
  readonly isCurrent?: () => boolean;
  readonly limit?: number;
  readonly location: (page: FeedPage, projectedMessages: readonly ChatItem[]) => EngineLocation | undefined;
  readonly mergeRequest: FeedPageMergeRequest;
  readonly onInstalled?: (result: InstalledFetchedWindow) => void | Promise<void>;
  readonly reason: string;
  readonly requestDebug?: Record<string, unknown>;
  readonly resetWindow?: boolean;
}

/** Orchestrates server, buffer, controller, engine, restore state, and live sync ownership. */
export class FeedSession {
  private readonly accountId: string;
  private readonly activeWireById = new Map<MessageId, WireMessage>();
  private readonly bootMessageJump: ((conversationId: ChannelId) => SortOrder | undefined) | undefined;
  private readonly buffer: FeedBufferLike;
  private readonly callbacks: FeedSessionCallbacks;
  private readonly controller: FeedController;
  private conversationId: ChannelId;
  private readonly edgePageThresholdPx: number;
  private readonly engine: FeedEngine<ChatItem>;
  private readonly entityStore: EntityStore;
  private readonly freshnessNow: () => number;
  private readonly freshnessRandom: () => number;
  private readonly freshnessScheduler: FeedFreshnessScheduler;
  private readonly isDocumentVisible: () => boolean;
  private readonly isOnline: () => boolean;
  private readonly loadedConversations = new Map<string, LoadedConversation>();
  private readonly pageSize: number;
  private readonly restoreScrollIdleDebounceMs: number;
  private readonly restoreStorage: FeedRestoreStorage | undefined;
  private server: FeedSessionServer;
  private readonly subscribers = new Set<(snapshot: FeedSessionSnapshot) => void>();
  private readonly viewerProfileId: ChatAuthor['id'];

  private restoreIdleTimer: ReturnType<typeof setTimeout> | undefined;
  private restorePersistenceReady = false;
  private returnToPresentPending = false;
  private running = false;
  private bootFirstWindowMarked = false;
  private snapshot: FeedSessionSnapshot;
  private olderLoadInFlight = false;
  private newerLoadInFlight = false;
  private freshnessInFlight: Promise<void> | undefined;
  private freshnessLastSucceededAt: number | undefined;
  private freshnessRetryResolve: (() => void) | undefined;
  private freshnessRetryTimer: ReturnType<typeof setTimeout> | undefined;
  private freshnessSnapshotIssued = false;
  private freshnessTrailing = false;
  private freshnessRetryReset = false;
  private bootLatestCachedEpoch: number | undefined;
  private preserveMountedWindowForFreshness = false;
  private startToken = 0;
  private storageWriteFailureEnabled = false;
  private unsubscribeLocalOptimistic: SocketUnsubscribe | undefined;
  private recentMessageEvents: RecentMessageEvent[] = [];
  private previousDistanceFromTop: number | undefined;
  private unsubscribeReactionDelta: SocketUnsubscribe | undefined;
  private unsubscribeController: (() => void) | undefined;
  private unsubscribeScroll: (() => void) | undefined;
  private unsubscribeSocket: SocketUnsubscribe | undefined;
  private trackedLoadedIds = new Set<MessageId>();

  /** Captures dependencies; start() performs subscription and boot work. */
  constructor(options: FeedSessionOptions) {
    this.accountId = options.accountId;
    this.bootMessageJump = options.bootMessageJump;
    this.buffer = options.buffer;
    this.callbacks = options.callbacks ?? {};
    this.controller = options.controller;
    this.conversationId = options.conversationId;
    this.edgePageThresholdPx = options.edgePageThresholdPx ?? DEFAULT_EDGE_PAGE_THRESHOLD_PX;
    this.engine = options.engine;
    this.entityStore = options.entityStore;
    this.freshnessNow = options.freshnessNow ?? Date.now;
    this.freshnessRandom = options.freshnessRandom ?? Math.random;
    this.freshnessScheduler = options.freshnessScheduler ?? { clearTimeout, setTimeout };
    this.isDocumentVisible = options.isDocumentVisible ?? (() =>
      typeof document === 'undefined' || document.visibilityState === 'visible'
    );
    this.isOnline = options.isOnline ?? (() => typeof navigator === 'undefined' || navigator.onLine !== false);
    this.pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
    this.restoreScrollIdleDebounceMs =
      options.restoreScrollIdleDebounceMs ?? DEFAULT_RESTORE_SCROLL_IDLE_DEBOUNCE_MS;
    this.restoreStorage = options.restoreStorage;
    this.server = options.server;
    this.viewerProfileId = options.viewerProfileId;
    this.snapshot = {
      bootLatestValidationState: IDLE_BOOT_LATEST_VALIDATION_STATE,
      bufferHealthy: this.buffer.isHealthy(),
      conversationId: this.conversationId,
      freshnessError: undefined,
      returnToPresentState: IDLE_RETURN_TO_PRESENT_STATE,
      status: 'Booting engine smoke route',
      storageWriteFailureEnabled: this.storageWriteFailureEnabled,
      unseenPresentCount: 0
    };
  }

  /** Returns the latest immutable session snapshot object. */
  getSnapshot(): FeedSessionSnapshot {
    return this.snapshot;
  }

  /** Subscribes immediately with the current snapshot and returns an idempotent unsubscribe. */
  subscribe(cb: (snapshot: FeedSessionSnapshot) => void): () => void {
    this.subscribers.add(cb);
    cb(this.snapshot);
    return () => this.subscribers.delete(cb);
  }

  /** Starts socket/scroll subscriptions and boots or restores the current conversation. */
  async start(): Promise<void> {
    if (this.running) return;

    this.running = true;
    this.previousDistanceFromTop = undefined;
    const token = this.startToken + 1;
    this.startToken = token;
    this.subscribeToServer();
    this.unsubscribeController = this.controller.subscribe((snapshot) => {
      const loadedIds = new Set(snapshot.items.flatMap((item) => item.id ? [item.id] : []));
      if ([...this.trackedLoadedIds].some((id) => !loadedIds.has(id))) {
        this.trimActiveWireToLoadedIds(loadedIds);
      }
      this.trackedLoadedIds = loadedIds;
    });
    this.unsubscribeScroll = this.engine.subscribeScrollState((state) => {
      const controllerSnapshot = this.controller.snapshot();
      const approachingOlderMessages =
        this.previousDistanceFromTop !== undefined && state.distanceFromTop < this.previousDistanceFromTop;
      const olderPageThresholdPx = approachingOlderMessages
        ? Math.max(this.edgePageThresholdPx, state.viewportHeight * APPROACHING_OLDER_PAGE_THRESHOLD_VIEWPORTS)
        : this.edgePageThresholdPx;
      this.previousDistanceFromTop = state.distanceFromTop;
      if (
        controllerSnapshot.olderCursor &&
        controllerSnapshot.edgeState.top !== 'error' &&
        state.distanceFromTop < olderPageThresholdPx
      ) {
        void this.loadOlder().catch((error: unknown) => {
          this.setStatus(`Engine older page failed: ${errorMessage(error)}`);
        });
      }

      if (
        controllerSnapshot.newerCursor &&
        controllerSnapshot.edgeState.bottom !== 'error' &&
        state.distanceFromBottom < this.edgePageThresholdPx
      ) {
        void this.loadNewer().catch((error: unknown) => {
          this.setStatus(`Engine newer page failed: ${errorMessage(error)}`);
        });
      }

      if (state.atBottom && !controllerSnapshot.newerCursor) {
        this.setUnseenPresentCount((count) => (count === 0 ? count : 0));
      }

      if (!this.restorePersistenceReady) return;
      this.scheduleRestorePersistence();
    });
    await this.bootOrRestore(token);
  }

  /** Stops subscriptions, invalidates in-flight boots, and clears restore timers. */
  stop(): void {
    this.running = false;
    this.startToken += 1;
    this.invalidateFreshness('stop');
    this.cancelFreshnessRetry();
    this.freshnessSnapshotIssued = false;
    this.freshnessTrailing = false;
    this.freshnessRetryReset = false;
    this.freshnessInFlight = undefined;
    this.bootLatestCachedEpoch = undefined;
    this.preserveMountedWindowForFreshness = false;
    this.recentMessageEvents = [];
    this.previousDistanceFromTop = undefined;
    this.unsubscribeFromServer();
    this.unsubscribeController?.();
    this.unsubscribeController = undefined;
    this.trackedLoadedIds.clear();
    this.unsubscribeScroll?.();
    this.unsubscribeScroll = undefined;
    this.clearRestoreTimer();
  }

  /** Publishes a display-ready status string to session subscribers. */
  setStatus(status: string): void {
    this.updateSnapshot({ status });
  }

  /** Publishes current buffer health or an explicit value after storage operations. */
  updateBufferHealth(healthy = this.buffer.isHealthy()): void {
    this.updateSnapshot({ bufferHealthy: healthy });
  }

  /** Refreshes the active conversation's canonical oldest row without resetting its window. */
  updateConversationStartSortOrder(sortOrder: SortOrder | undefined): void {
    this.server.conversationStartSortOrder = sortOrder;
    if (sortOrder && !this.hasLoadedConversationStart()) {
      this.controller.reopenOlderFromLoadedStart();
    }
  }

  /** Revalidates the in-memory mirror; buffer failures go through reportBufferError. */
  async revalidateMirror(): Promise<void> {
    try {
      await this.buffer.revalidateMirror(this.accountId, this.conversationId);
      this.updateBufferHealth();
    } catch (error: unknown) {
      this.reportBufferError(error);
    }
  }

  /** Toggles simulated durable write failure and updates health/status channels. */
  setStorageWriteFailure(enabled: boolean): void {
    this.buffer.setSimulateWriteFailure(enabled);
    this.storageWriteFailureEnabled = enabled;
    this.updateSnapshot({
      bufferHealthy: this.buffer.isHealthy(),
      status: enabled ? 'Storage write failure simulation enabled' : 'Storage write failure simulation disabled',
      storageWriteFailureEnabled: enabled
    });
    this.recordDebug('buffer:simulate-write-failure', { enabled, healthy: this.buffer.isHealthy() });
  }

  /** Flips the simulated durable write failure flag. */
  toggleStorageWriteFailure(): void {
    this.setStorageWriteFailure(!this.storageWriteFailureEnabled);
  }

  /** Removes the saved restore record for this account/conversation and updates status. */
  clearRestoreRecord(): void {
    this.restoreStorage?.remove(this.accountId, this.conversationId);
    this.setStatus('Cleared saved restore');
  }

  /** Persists the current anchor or latest marker; returns false when no restorable target exists. */
  persistRestoreNow(reason: 'idle' | 'manual' | 'pagehide' = 'idle'): boolean {
    this.clearRestoreTimer();
    const announce = reason === 'manual';
    if (!this.restorePersistenceReady) {
      if (announce) this.setStatus('No restorable anchor is visible');
      return false;
    }

    const scrollState = this.engine.getScrollState();
    if (scrollState.mode === 'AT_BOTTOM' && !this.controller.snapshot().newerCursor) {
      this.restoreStorage?.write({
        version: 1,
        accountId: this.accountId,
        conversationId: this.conversationId,
        mode: 'latest',
        savedAt: Date.now()
      });
      if (announce) this.setStatus('Saved restore at latest');
      return true;
    }

    const snapshot = this.engine.getAnchorSnapshot();
    const parsedSnapshotId = parseMessageId(snapshot.id);
    if (!parsedSnapshotId.ok || !snapshot.itemKey) {
      if (announce) this.setStatus('No restorable anchor is visible');
      return false;
    }

    const anchorItem = this.controller.snapshot().items.find((item) =>
      item.itemKey === snapshot.itemKey || item.id === parsedSnapshotId.value
    );
    if (!anchorItem?.sortOrder) {
      if (announce) this.setStatus('No restorable anchor is visible');
      return false;
    }

    this.restoreStorage?.write({
      version: 1,
      accountId: this.accountId,
      conversationId: this.conversationId,
      mode: 'anchor',
      savedAt: Date.now(),
      id: parsedSnapshotId.value,
      sortOrder: anchorItem.sortOrder,
      itemKey: snapshot.itemKey,
      offsetPx: snapshot.offsetPx
    });
    if (announce) this.setStatus(`Saved restore anchor ${parsedSnapshotId.value}`);
    return true;
  }

  /** Sends composer text with optimistic UI; all failures are reported through status. */
  async sendComposerMessage(body: string): Promise<void> {
    const shouldReplaceWindow = this.controller.isHistoricWindow();
    const clientRequestId = createClientRequestId();
    const optimistic = createSendingChatItem({
      itemKey: optimisticItemKey(clientRequestId),
      clientRequestId,
      author: this.viewerAuthor(),
      createdAt: new Date().toISOString(),
      body,
      attachments: [],
      previews: [],
      reactions: []
    });
    if (!shouldReplaceWindow) {
      this.controller.applyOptimisticSend(optimistic, 'force');
    }
    const markSendFailed = (detail: string) => {
      if (!shouldReplaceWindow) {
        this.controller.markSendFailed(clientRequestId);
      }
      this.setStatus(detail);
    };

    let sentId: MessageId | undefined;
    try {
      sentId = await this.server.send(clientRequestId, body);
    } catch (error: unknown) {
      markSendFailed(`Composer send failed: ${errorMessage(error)}`);
      return;
    }

    if (!sentId) {
      markSendFailed('Composer send failed');
      return;
    }

    if (shouldReplaceWindow || this.controller.isHistoricWindow()) {
      try {
        const installedLatest = await this.replaceWithLatestWindow();
        if (installedLatest) {
          this.setStatus(`Composer sent ${sentId} and jumped to latest`);
        }
      } catch (error: unknown) {
        const detail = errorMessage(error);
        this.setStatus(`Composer sent ${sentId}; latest refresh failed: ${detail}`);
      }
      return;
    }

    this.controller.scrollTo({ kind: 'bottom' }, 'smooth');
    this.setStatus(`Composer sent ${sentId}`);
  }

  /** Applies a locally-dispatched optimistic row from the legacy composer path. */
  applyLocalOptimisticMessage(message: LocalOptimisticMessage): boolean {
    this.recordDebug('send:local-optimistic', {
      clientRequestId: message.clientRequestId,
      forChannel: message.channelId === this.conversationId,
      historic: this.controller.isHistoricWindow()
    });
    if (message.channelId !== this.conversationId) return false;

    if (this.controller.isHistoricWindow()) {
      void this.replaceWithLatestWindow()
        .then((installedLatest) => {
          if (installedLatest) this.setStatus('Composer send jumped to latest');
        })
        .catch((error: unknown) => {
          this.setStatus(`Composer send latest refresh failed: ${errorMessage(error)}`);
        });
      return false;
    }

    const now = new Date();
    const optimistic = createSendingChatItem({
      itemKey: optimisticItemKey(message.clientRequestId),
      clientRequestId: message.clientRequestId,
      author: this.authorForProfile(message.authorId),
      createdAt: now.toISOString(),
      rendererRow: optimisticRendererRow(message, now),
      body: message.body,
      attachments: [],
      previews: [],
      reactions: []
    });
    this.controller.applyOptimisticSend(optimistic, 'force');
    this.recordDebug('send:optimistic-appended', {
      itemKey: optimistic.itemKey,
      items: this.controller.snapshot().items.length
    });
    this.setStatus('Composer sending');
    return true;
  }

  /** Applies a per-reaction live delta to a loaded message, preserving unrelated reactions. */
  applyReactionDelta(action: ReactionDeltaAction, reaction: LiveWireReaction): boolean {
    if (reaction.channelId !== this.conversationId) return false;
    if (!this.controller.snapshot().items.some((item) => item.id === reaction.messageId)) return false;

    const current = this.activeWireById.get(reaction.messageId);
    if (!current) return false;

    const nextReactions = patchWireReactions(current.reactions, action, reaction);
    if (nextReactions === current.reactions) return false;

    const nextWire: WireMessage = {
      ...current,
      reactions: nextReactions,
      rendererRow: patchRendererRowReaction(current, action, reaction)
    };
    this.activeWireById.set(nextWire.id, nextWire);
    return this.controller.applyLoadedUpdate(projectMessage(nextWire, this.entityStore));
  }

  /** Ingests entity snapshots and re-projects loaded rows that reference changed entities. */
  applyEntityBundle(bundle: EntityBundle): number {
    const changes = this.entityStore.ingest(bundle);
    if (!hasEntityChanges(changes)) return 0;

    let affectedCount = 0;
    for (const workspaceId of loadedWorkspaceIds(this.loadedConversations, this.accountId)) {
      affectedCount += this.applyEntityChanges({
        accountId: this.accountId,
        workspaceId,
        entityRevision: this.buffer.entityRevision(this.accountId, workspaceId),
        changes
      });
    }
    if (affectedCount > 0) {
      this.setStatus(`Engine re-projected ${affectedCount} rows for entity update`);
    }
    return affectedCount;
  }

  /** Returns to live latest content or scrolls to bottom when already live-contiguous. */
  async jumpToLatest(): Promise<void> {
    if (this.returnToPresentPending) return;

    if (!this.controller.isHistoricWindow()) {
      this.setReturnToPresentState(IDLE_RETURN_TO_PRESENT_STATE);
      this.setUnseenPresentCount(0);
      this.controller.scrollTo({ kind: 'bottom' }, 'smooth');
      this.setStatus('Engine jumped to latest');
      return;
    }

    this.returnToPresentPending = true;
    this.setReturnToPresentState({ kind: 'loading' });
    this.setStatus('Engine loading latest page');
    let nextState: ReturnToPresentState = IDLE_RETURN_TO_PRESENT_STATE;
    try {
      const installedLatest = await this.replaceWithLatestWindow();
      if (installedLatest) {
        this.setUnseenPresentCount(0);
        this.setStatus('Engine jumped to latest');
      }
    } catch (error: unknown) {
      const message = errorMessage(error);
      nextState = { kind: 'error', message };
      this.setStatus(`Engine latest page failed: ${message}`);
    } finally {
      this.returnToPresentPending = false;
      this.setReturnToPresentState(nextState);
    }
  }

  /** Centers a message in the active window, fetching an around slice when it is not loaded. */
  async jumpToMessage(input: FeedMessageJumpInput): Promise<boolean> {
    const align = input.align ?? 'center';
    const loadedTarget = this.findLoadedJumpTarget(input);
    if (loadedTarget) {
      this.controller.scrollTo({ kind: 'item', key: loadedTarget.itemKey, align }, 'smooth');
      this.setStatus(`Engine jumped to loaded message ${loadedTarget.id ?? input.sortOrder}`);
      return true;
    }

    this.setStatus(`Engine jumping to message ${input.sortOrder}`);
    const aroundAnchor: FeedAroundAnchor = {
      ...(input.id === undefined ? {} : { id: input.id }),
      sortOrder: input.sortOrder
    };
    let target: { key: string } | undefined;
    const result = await this.installFetchedWindow({
      cursors: (page) => ({
        olderCursor: page.olderCursor,
        newerCursor: page.newerCursor,
        syncCursor: page.syncCursor
      }),
      direction: 'around',
      fetch: () => this.server.around(aroundAnchor, this.pageSize),
      location: (page, projectedMessages) => {
        target = resolveJumpTarget(projectedMessages, page, input);
        return target ? { kind: 'item', key: target.key, align } : undefined;
      },
      mergeRequest: { kind: 'around' },
      onInstalled: () => {
        this.restorePersistenceReady = true;
        this.setBootLatestValidationState(IDLE_BOOT_LATEST_VALIDATION_STATE);
        this.setUnseenPresentCount(0);
        this.setStatus(`Engine jumped to message ${target?.key ?? input.sortOrder}`);
      },
      reason: 'message-jump',
      requestDebug: { id: input.id, sortOrder: input.sortOrder },
      resetWindow: true
    });
    if (result.kind === 'error') {
      this.setStatus(`Engine message jump failed: ${errorMessage(result.error)}`);
      return false;
    }
    if (result.kind === 'missing-location') {
      this.setStatus(`Engine message jump target missing ${input.sortOrder}`);
      return false;
    }
    return result.kind === 'installed';
  }

  /** Returns the loaded item for side-channel enrichment paths such as reply previews. */
  getLoadedItem(id: MessageId): ChatItem | undefined {
    return this.controller.snapshot().items.find((item) => item.id === id);
  }

  /** Updates the legacy renderer row for one loaded server-backed message. */
  applyLoadedRendererRowUpdate(id: MessageId, rendererRow: unknown): boolean {
    const item = this.getLoadedItem(id);
    if (!item) return false;

    const wire = this.activeWireById.get(id);
    if (wire) {
      this.activeWireById.set(id, {
        ...wire,
        rendererRow
      });
    }

    return this.controller.applyLoadedUpdate({
      ...item,
      rendererRow
    });
  }

  /** Retries failed boot latest validation for a warm cached window. */
  async retryBootLatest(): Promise<void> {
    if (this.snapshot.bootLatestValidationState.kind !== 'error') return;

    const token = this.startToken;
    if (!this.isCurrentStart(token)) return;

    const cachedEpoch = this.controller.snapshot().windowEpoch;
    // Same presumed-live edge state as showCachedLatest while validation is in flight.
    if (!this.controller.setEdgesIfCurrent(cachedEpoch, { bottom: 'live' })) return;
    this.bootLatestCachedEpoch = cachedEpoch;
    this.setBootLatestValidationState({ kind: 'loading' });
    await this.ensureFresh('manual-retry');
    if (this.isCurrentStart(token) && this.snapshot.freshnessError) {
      this.handleBootLatestValidationFailure(cachedEpoch, this.snapshot.freshnessError);
    }
  }

  /** Loads a historic slice around a mock index when the server exposes demo indexing helpers. */
  async jumpToHistoricIndex(targetIndex?: number): Promise<void> {
    if (!this.server.tailIndex || !this.server.messageAt) {
      this.setStatus('Engine historic jump is unavailable for this source');
      return;
    }

    const resolvedTargetIndex = targetIndex ?? Math.max(0, this.server.tailIndex() - 720);
    const targetId = this.server.messageAt(resolvedTargetIndex).id;
    if (!targetId) return;

    this.setStatus(`Engine jumping to historic slice ${resolvedTargetIndex}`);
    await this.installFetchedWindow({
      cursors: (page) => ({
        olderCursor: page.olderCursor,
        newerCursor: page.newerCursor,
        syncCursor: page.syncCursor
      }),
      direction: 'around',
      fetch: () => this.server.around(targetId, 80),
      limit: 80,
      location: (page) => ({ kind: 'item', key: page.resolvedId ?? targetId, align: 'center' }),
      mergeRequest: { kind: 'around' },
      onInstalled: () => {
        this.setBootLatestValidationState(IDLE_BOOT_LATEST_VALIDATION_STATE);
        this.setUnseenPresentCount(0);
        this.setStatus(`Engine jumped to historic slice around ${resolvedTargetIndex}`);
      },
      reason: 'jump-history',
      requestDebug: { id: targetId, targetIndex: resolvedTargetIndex },
      resetWindow: true
    });
  }

  /** Loads the older edge page from cache or server; guarded errors become status updates. */
  async loadOlder(): Promise<void> {
    // Re-entrancy latch: engine flushes emit scroll state synchronously inside apply
    // batches, which re-triggers this method before the outer call finishes. The async
    // network path is guarded by guardedPageFetch, but the synchronous warm-mirror path
    // needs its own latch or it recurses.
    if (this.olderLoadInFlight) {
      this.recordDebug('pagination:older:reentrant-skip', {});
      return;
    }
    this.olderLoadInFlight = true;
    try {
      await this.loadOlderInner();
    } finally {
      this.olderLoadInFlight = false;
    }
  }

  private async loadOlderInner(): Promise<void> {
    const membershipGeneration = this.buffer.membershipGeneration(this.accountId);
    const snapshot = this.controller.snapshot();
    let syncMiss:
      | {
          readonly cursor: SortOrder;
          readonly result: Extract<LocalPageResult, { readonly kind: 'miss' }>;
        }
      | undefined;
    if (snapshot.olderCursor) {
      const local = this.buffer.beforeSync(this.accountId, this.conversationId, snapshot.olderCursor, 24);
      if (local?.kind === 'hit') {
        this.recordDebug('pagination:older:cache-sync', {
          cursor: snapshot.olderCursor,
          count: local.page.messages.length
        });
        if (this.acceptsProgressiveCachePage('older', 'cache-sync', snapshot.olderCursor, local.page)) {
          const projectedMessages = this.projectPage(local.page);
          const insertedIds = insertedMessageIds(projectedMessages, this.controller.snapshot().items);
          const prepended = this.controller.applyOlderPage(projectedMessages, local.page.olderCursor);
          await this.replayRecentMessageEventsAfterSnapshot(local.page.syncCursor, {
            debugKind: 'pagination:older:replay',
            messageIds: insertedIds
          });
          this.setStatus(
            local.page.olderCursor
              ? `Engine prepended ${prepended} older messages (cache)`
              : `Engine reached beginning with ${prepended} older messages (cache)`
          );
          return;
        }
      }
      if (local?.kind === 'end') {
        if (this.hasLoadedConversationStart()) {
          this.recordDebug('pagination:older:cache-sync-end', { cursor: snapshot.olderCursor });
          this.controller.applyOlderPage([], undefined);
          this.setStatus('Engine reached beginning with 0 older messages (cache)');
          return;
        }
        this.recordDebug('pagination:older:cache-sync-end-rejected', {
          cursor: snapshot.olderCursor,
          expectedStart: this.server.conversationStartSortOrder
        });
      }
      if (local?.kind === 'miss') {
        syncMiss = { cursor: snapshot.olderCursor, result: local };
      }
    }

    this.setStatus('Engine loading older page');
    let mergeRevoked = false;
    let pageSource: 'cache' | 'memory-only' | 'network' | 'stale-revalidate' = 'network';
    let removedMessageIds: MessageId[] = [];
    const result = await this.controller.guardedPageFetch({
      direction: 'older',
      fetchPage: async ({ cursor }) => {
        if (!cursor) {
          pageSource = 'cache';
          return emptyFeedPage();
        }

        const local = syncMiss?.cursor === cursor
          ? syncMiss.result
          : await this.buffer.before(this.accountId, this.conversationId, cursor, 24);
        if (local.kind === 'hit') {
          if (this.acceptsProgressiveCachePage('older', 'cache', cursor, local.page)) {
            pageSource = 'cache';
            this.recordDebug('pagination:older:cache', { cursor, count: local.page.messages.length });
            return local.page;
          }
        }
        if (local.kind === 'end') {
          if (this.hasLoadedConversationStart()) {
            pageSource = 'cache';
            this.recordDebug('pagination:older:cache-end', { cursor });
            return emptyFeedPage();
          }
          this.recordDebug('pagination:older:cache-end-rejected', {
            cursor,
            expectedStart: this.server.conversationStartSortOrder
          });
        }

        pageSource = 'network';
        const page = await this.server.before(cursor, 24);
        const reason = local.kind === 'miss' ? local.reason : 'no-progress';
        if (reason === 'stale') pageSource = 'stale-revalidate';
        this.recordDebug('pagination:older:network', { cursor, count: page.messages.length, reason });
        const mergeResult = await this.buffer.mergePage(
          this.accountId,
          this.conversationId,
          { kind: 'before', boundarySortOrder: cursor },
          page,
          membershipGeneration
        );
        if (mergeResult.revoked) {
          mergeRevoked = true;
          return emptyFeedPage();
        }
        removedMessageIds = mergeResult.removedIds;
        if (!mergeResult.persisted) {
          pageSource = 'memory-only';
          this.updateBufferHealth();
          this.recordDebug('buffer:merge-page-failed', { direction: 'older', cursor });
        }
        return page;
      },
      limit: 24,
      loadingSide: 'older',
      pageDebug
    });

    if (result.kind === 'skipped') {
      if (result.reason === 'no-cursor') this.setStatus('Engine reached beginning');
      return;
    }
    if (result.kind === 'dropped') return;
    if (result.kind === 'error') {
      this.setStatus(`Engine older page failed: ${errorMessage(result.error)}`);
      return;
    }
    if (mergeRevoked) {
      this.handleRevokedMerge({ direction: 'older', cursor: snapshot.olderCursor });
      return;
    }
    if (!this.controller.isEpochCurrent(result.epoch)) return;

    const projectedMessages = this.projectPage(result.page);
    const insertedIds = insertedMessageIds(projectedMessages, this.controller.snapshot().items);
    const prepended = this.controller.applyOlderPage(projectedMessages, result.page.olderCursor);
    if (removedMessageIds.length > 0) {
      this.controller.removeLoadedItems(removedMessageIds);
    }
    await this.replayRecentMessageEventsAfterSnapshot(result.page.syncCursor, {
      debugKind: 'pagination:older:replay',
      messageIds: insertedIds,
      sortOrders: sortOrdersForMessageIds(projectedMessages, insertedIds)
    });
    this.setStatus(
      result.page.olderCursor
        ? `Engine prepended ${prepended} older messages (${pageSource})`
        : `Engine reached beginning with ${prepended} older messages (${pageSource})`
    );
  }

  private hasLoadedConversationStart(): boolean {
    const expectedStart = this.server.conversationStartSortOrder;
    if (!expectedStart) return true;

    return this.controller.snapshot().items.some((item) =>
      item.sortOrder !== undefined && compareSortOrders(item.sortOrder, expectedStart) <= 0
    );
  }

  /** Loads the newer edge page from cache or server; guarded errors become status updates. */
  async loadNewer(): Promise<void> {
    if (this.newerLoadInFlight) {
      this.recordDebug('pagination:newer:reentrant-skip', {});
      return;
    }
    this.newerLoadInFlight = true;
    try {
      await this.loadNewerInner();
    } finally {
      this.newerLoadInFlight = false;
    }
  }

  private async loadNewerInner(): Promise<void> {
    const membershipGeneration = this.buffer.membershipGeneration(this.accountId);
    const snapshot = this.controller.snapshot();
    let syncMiss:
      | {
          readonly cursor: SortOrder;
          readonly result: Extract<LocalPageResult, { readonly kind: 'miss' }>;
        }
      | undefined;
    if (snapshot.newerCursor) {
      const local = this.buffer.afterSync(this.accountId, this.conversationId, snapshot.newerCursor, 24);
      if (local?.kind === 'hit') {
        this.recordDebug('pagination:newer:cache-sync', {
          cursor: snapshot.newerCursor,
          count: local.page.messages.length
        });
        if (this.acceptsProgressiveCachePage('newer', 'cache-sync', snapshot.newerCursor, local.page)) {
          const projectedMessages = this.projectPage(local.page);
          const insertedIds = insertedMessageIds(projectedMessages, this.controller.snapshot().items);
          const appended = this.controller.applyNewerPage(projectedMessages, local.page.newerCursor);
          if (!local.page.newerCursor) {
            this.setUnseenPresentCount(0);
          }
          await this.replayRecentMessageEventsAfterSnapshot(local.page.syncCursor, {
            debugKind: 'pagination:newer:replay',
            messageIds: insertedIds
          });
          this.setStatus(
            local.page.newerCursor
              ? `Engine appended ${appended} newer messages (cache)`
              : `Engine reached present with ${appended} newer messages (cache)`
          );
          return;
        }
      }
      if (local?.kind === 'end') {
        this.recordDebug('pagination:newer:cache-sync-end', { cursor: snapshot.newerCursor });
        this.controller.applyNewerPage([], undefined);
        this.setUnseenPresentCount(0);
        this.setStatus('Engine reached present with 0 newer messages (cache)');
        return;
      }
      if (local?.kind === 'miss') {
        syncMiss = { cursor: snapshot.newerCursor, result: local };
      }
    }

    this.setStatus('Engine loading newer page');
    let mergeRevoked = false;
    let pageSource: 'cache' | 'memory-only' | 'network' | 'stale-revalidate' = 'network';
    let removedMessageIds: MessageId[] = [];
    const result = await this.controller.guardedPageFetch({
      direction: 'newer',
      fetchPage: async ({ cursor }) => {
        if (!cursor) {
          pageSource = 'cache';
          return emptyFeedPage();
        }

        const local = syncMiss?.cursor === cursor
          ? syncMiss.result
          : await this.buffer.after(this.accountId, this.conversationId, cursor, 24);
        if (local.kind === 'hit') {
          if (this.acceptsProgressiveCachePage('newer', 'cache', cursor, local.page)) {
            pageSource = 'cache';
            this.recordDebug('pagination:newer:cache', { cursor, count: local.page.messages.length });
            return local.page;
          }
        }
        if (local.kind === 'end') {
          pageSource = 'cache';
          this.recordDebug('pagination:newer:cache-end', { cursor });
          return emptyFeedPage();
        }

        pageSource = 'network';
        const page = await this.server.after(cursor, 24);
        const reason = local.kind === 'miss' ? local.reason : 'no-progress';
        if (reason === 'stale') pageSource = 'stale-revalidate';
        this.recordDebug('pagination:newer:network', { cursor, count: page.messages.length, reason });
        const mergeResult = await this.buffer.mergePage(
          this.accountId,
          this.conversationId,
          { kind: 'after', boundarySortOrder: cursor },
          page,
          membershipGeneration
        );
        if (mergeResult.revoked) {
          mergeRevoked = true;
          return emptyFeedPage();
        }
        removedMessageIds = mergeResult.removedIds;
        if (!mergeResult.persisted) {
          pageSource = 'memory-only';
          this.updateBufferHealth();
          this.recordDebug('buffer:merge-page-failed', { direction: 'newer', cursor });
        }
        return page;
      },
      limit: 24,
      loadingSide: 'newer',
      pageDebug
    });

    if (result.kind === 'skipped') {
      if (result.reason === 'no-cursor') this.setStatus('Engine is at present');
      return;
    }
    if (result.kind === 'dropped') return;
    if (result.kind === 'error') {
      this.setStatus(`Engine newer page failed: ${errorMessage(result.error)}`);
      return;
    }
    if (mergeRevoked) {
      this.handleRevokedMerge({ direction: 'newer', cursor: snapshot.newerCursor });
      return;
    }
    if (!this.controller.isEpochCurrent(result.epoch)) return;

    const projectedMessages = this.projectPage(result.page);
    const insertedIds = insertedMessageIds(projectedMessages, this.controller.snapshot().items);
    const appended = this.controller.applyNewerPage(projectedMessages, result.page.newerCursor);
    if (removedMessageIds.length > 0) {
      this.controller.removeLoadedItems(removedMessageIds);
    }
    if (!result.page.newerCursor) {
      this.setUnseenPresentCount(0);
    }
    await this.replayRecentMessageEventsAfterSnapshot(result.page.syncCursor, {
      debugKind: 'pagination:newer:replay',
      messageIds: insertedIds,
      sortOrders: sortOrdersForMessageIds(projectedMessages, insertedIds)
    });
    this.setStatus(
      result.page.newerCursor
        ? `Engine appended ${appended} newer messages (${pageSource})`
        : `Engine reached present with ${appended} newer messages (${pageSource})`
    );
  }

  /** Demo helper that drops the socket and leaves events queued for a later resync. */
  dropSocketForResync(): void {
    if (!this.server.resync) {
      this.setStatus('Engine socket drop is unavailable for this source');
      return;
    }

    this.server.resync.dropAndAccumulate();
    this.setStatus('Engine socket dropped; events will accumulate until resync');
  }

  /** Invalidates the low-evidence activation TTL after evidence of a delivery gap. */
  invalidateFreshness(reason: string): void {
    this.freshnessLastSucceededAt = undefined;
    this.recordDebug('freshness:invalidate', { reason });
  }

  /** Reconciles journal changes and an authoritative latest snapshot with bounded retry. */
  ensureFresh(reason: FeedFreshnessReason): Promise<void> {
    const lowEvidence = reason === 'focus' || reason === 'visible' || reason === 'heartbeat';
    const now = this.freshnessNow();
    if (
      lowEvidence &&
      this.freshnessLastSucceededAt !== undefined &&
      now - this.freshnessLastSucceededAt < FEED_FRESHNESS_TTL_MS &&
      !this.freshnessRetryTimer
    ) {
      this.recordDebug('freshness:skip', { reason, ttl: true });
      return Promise.resolve();
    }

    if (!lowEvidence) {
      this.invalidateFreshness(reason);
      this.freshnessRetryReset = true;
      this.cancelFreshnessRetry();
    }
    if (this.freshnessInFlight) {
      if (this.freshnessSnapshotIssued) this.freshnessTrailing = true;
      this.recordDebug('freshness:coalesced', {
        reason,
        trailing: this.freshnessTrailing
      });
      return this.freshnessInFlight;
    }

    const pending = this.runFreshnessCycle(reason).finally(() => {
      if (this.freshnessInFlight === pending) this.freshnessInFlight = undefined;
    });
    this.freshnessInFlight = pending;
    return pending;
  }

  /** Backward-compatible demo/test entrypoint; production lifecycle uses ensureFresh. */
  async resync(_reason: string): Promise<void> {
    await this.ensureFresh('manual-retry');
  }

  /** Demo helper that reconnects the mock socket before running the production resync path. */
  async resyncFromServer(): Promise<void> {
    this.server.resync?.reconnect();
    await this.resync('demo-reconnect');
  }

  /** Demo helper that forces sync overflow once and then restores normal server behavior. */
  async simulateSyncOverflow(): Promise<void> {
    if (!this.server.resync) {
      this.setStatus('Engine sync overflow simulation is unavailable for this source');
      return;
    }

    this.setStatus('Engine simulating sync overflow');
    this.server.resync.forceOverflow = true;
    try {
      await this.resync('demo-overflow');
    } catch (error: unknown) {
      this.setStatus(`Engine sync overflow failed: ${errorMessage(error)}`);
    } finally {
      this.server.resync.forceOverflow = false;
    }
  }

  /** Applies a sync result; returns counts when events were applied, undefined on overflow/drop/failure. */
  async applyServerSyncResult(
    cursor: SyncCursor,
    result: SyncResult,
    isCurrent: () => boolean = () => true
  ): Promise<ResyncApplyResult | undefined> {
    if (result.overflow) {
      await this.applySyncOverflow(cursor, result.nextCursor, isCurrent);
      return undefined;
    }

    const representedBefore = this.controller.snapshot().items;
    const applied = await this.applyResyncEvents(result.events, result.nextCursor, isCurrent);
    if (!isCurrent()) return undefined;
    if (!applied) {
      throw new Error('sync events could not be persisted');
    }

    const represented = [...representedBefore, ...this.controller.snapshot().items];
    await this.replayRecentMessageEventsAfterSnapshot(result.nextCursor, {
      debugKind: 'sync:replay-live-race',
      isCurrent,
      messageIds: new Set(represented.flatMap((item) => item.id ? [item.id] : [])),
      sortOrders: new Set(represented.flatMap((item) => item.sortOrder ? [item.sortOrder] : [])),
    });
    if (!isCurrent()) return undefined;

    this.recordDebug('sync:ok', {
      cursor,
      eventCount: result.events.length,
      nextCursor: result.nextCursor,
      removed: applied.diff.removed,
      updated: applied.diff.updated,
      appended: applied.diff.appended,
      withheldHistoricCreates: applied.diff.withheldHistoricCreates,
      ackedSends: applied.ackedSends,
      entityFanoutCount: applied.entityFanoutCount
    });
    this.setStatus(
      `Engine resynced ${applied.eventCount} events: ` +
      `${applied.diff.removed} removed, ${applied.diff.updated} updated, ` +
      `${applied.diff.appended} appended, ${applied.diff.withheldHistoricCreates} withheld`
    );
    return applied;
  }

  /** Purges durable account cache and attempts to reload latest; failures become status updates. */
  async purgeAccountCache(): Promise<void> {
    this.invalidateFreshness('account-cache-purge');
    this.setStatus('Engine purging account cache');
    try {
      await this.buffer.purgeAccount(this.accountId);
      this.activeWireById.clear();
      this.loadedConversations.clear();
      const reloaded = await this.replaceWithLatestWindow();
      this.setStatus(reloaded ? 'Engine purged account cache and reloaded latest' : 'Engine purged account cache');
    } catch (error: unknown) {
      this.setStatus(`Engine purge failed: ${errorMessage(error)}`);
    }
  }

  /** Revokes durable account cache and clears loaded UI state; failures become status updates. */
  async revokeAccount(): Promise<void> {
    this.setStatus('Engine revoking account');
    try {
      await this.buffer.revokeAccount(this.accountId);
      this.clearSignedOutState();
    } catch (error: unknown) {
      this.setStatus(`Engine revoke failed: ${errorMessage(error)}`);
    }
  }

  /** Switches conversation/server, saves the old restore point, and boots the new window. */
  async switchConversation(conversationId: ChannelId, server = this.server): Promise<void> {
    if (conversationId === this.conversationId && server === this.server) return;

    const oldConversationId = this.conversationId;
    if (this.controller.snapshot().items.length > 0) {
      this.persistRestoreNow('pagehide');
    }

    this.clearRestoreTimer();
    this.invalidateFreshness('conversation-switch');
    this.cancelFreshnessRetry();
    this.freshnessSnapshotIssued = false;
    this.freshnessTrailing = false;
    this.freshnessRetryReset = false;
    this.freshnessInFlight = undefined;
    this.bootLatestCachedEpoch = undefined;
    this.preserveMountedWindowForFreshness = false;
    this.previousDistanceFromTop = undefined;
    const token = this.startToken + 1;
    this.startToken = token;
    // No engine API changes for conversation switching: message ids remain
    // globally unique itemKeys, while the engine height LRU bounds retained rows.
    this.controller.replaceWindow([], { kind: 'bottom' }, {});

    const serverChanged = server !== this.server;
    if (serverChanged) {
      this.unsubscribeFromServer();
      this.server = server;
      if (this.running) {
        this.subscribeToServer();
      }
    }

    this.conversationId = conversationId;
    this.activeWireById.clear();
    this.loadedConversations.delete(loadedConversationKey(this.accountId, oldConversationId));
    this.recentMessageEvents = [];
    this.restorePersistenceReady = false;
    this.returnToPresentPending = false;
    this.setBootLatestValidationState(IDLE_BOOT_LATEST_VALIDATION_STATE);
    this.setReturnToPresentState(IDLE_RETURN_TO_PRESENT_STATE);
    this.setUnseenPresentCount(0);
    this.updateSnapshot({ conversationId, freshnessError: undefined });

    if (!this.isCurrentStart(token)) return;
    await this.bootOrRestore(token);
  }

  private async bootOrRestore(token: number): Promise<void> {
    // A pending message jump owns the boot: install the around-target window
    // as the first paint instead of booting to latest/restore and re-centering
    // afterwards, which painted the wrong position and then visibly jumped.
    // A failed jump boot (missing target, fetch error) falls through to the
    // standard boot so the channel still comes up.
    const bootJumpSortOrder = this.bootMessageJump?.(this.conversationId);
    if (bootJumpSortOrder !== undefined) {
      const bootJumpConversationId = this.conversationId;
      this.beginBootPerformanceCycle('jump');
      const jumped = await this.jumpToMessage({ sortOrder: bootJumpSortOrder, align: 'center' });
      if (!this.isCurrentStart(token)) {
        // The consume raced a supersession (unmount or quick conversation
        // switch), so nothing will drain this intent again: hand it back so
        // a successor boot within the TTL can still land it.
        this.callbacks.onBootMessageJumpAbandoned?.(bootJumpConversationId, bootJumpSortOrder);
        return;
      }
      if (jumped) {
        this.recordBootFirstWindow('jump', this.controller.snapshot().items.length);
        this.callbacks.onBootMessageJumpInstalled?.(bootJumpConversationId, bootJumpSortOrder);
        return;
      }
    }

    const savedRestore = validatedRestoreRecord(
      this.restoreStorage?.read(this.accountId, this.conversationId),
      this.accountId,
      this.conversationId
    );
    this.beginBootPerformanceCycle(savedRestore?.mode === 'anchor' ? 'restore' : 'latest');
    if (savedRestore?.mode !== 'latest' && savedRestore) {
      await this.restoreHistoric(savedRestore, token);
      return;
    }

    await this.bootLatestWindow(token);
  }

  private async restoreHistoric(savedRestore: FeedAnchorRestoreRecord, token: number): Promise<void> {
    this.setStatus(`Engine restoring saved slice from ${this.sourceLabel()}`);
    const cachedPreviewInstalled = await this.showCachedAnchor(savedRestore, token);
    if (!this.isCurrentStart(token)) return;

    let restoreTarget: { key: string; offsetPx: number } | undefined;
    const result = await this.installFetchedWindow({
      cursors: (page) => ({
        olderCursor: page.olderCursor,
        newerCursor: page.newerCursor,
        syncCursor: page.syncCursor
      }),
      direction: 'around',
      fetch: () => this.server.around({
        id: savedRestore.id,
        sortOrder: savedRestore.sortOrder
      }, this.pageSize),
      isCurrent: () => this.isCurrentStart(token),
      location: (page, projectedMessages) => {
        restoreTarget = resolveRestoreTarget(projectedMessages, page, savedRestore);
        return restoreTarget
          ? { kind: 'item', key: restoreTarget.key, align: 'start', offsetPx: restoreTarget.offsetPx }
          : undefined;
      },
      mergeRequest: { kind: 'around' },
      onInstalled: ({ projectedMessages }) => {
        this.restorePersistenceReady = true;
        this.recordBootFirstWindow('restore', projectedMessages.length);
        this.recordDebug('performance:restore:anchored', {
          key: restoreTarget?.key,
          sortOrder: savedRestore.sortOrder
        });
        this.setStatus(`Engine restored ${restoreTarget?.key ?? savedRestore.id}`);
      },
      reason: 'restore',
      requestDebug: { id: savedRestore.id, sortOrder: savedRestore.sortOrder },
      resetWindow: true
    });
    if (result.kind === 'error') {
      if (cachedPreviewInstalled) {
        // Same failure affordance as a cached-latest boot: bottom edge flips to
        // error and the edge zone's retry runs the freshness cycle. The fetch's
        // resetWindow bumped the epoch past the preview's, so target the
        // current one — the error return already guarantees the start is
        // current, and nothing else has replaced the preview window.
        this.handleBootLatestValidationFailure(this.controller.snapshot().windowEpoch, result.error);
        this.setStatus(`Engine restore validation failed: ${errorMessage(result.error)}`);
        return;
      }
      await this.bootLatestWindow(token);
      return;
    }
    if (result.kind === 'missing-location') {
      this.setStatus(cachedPreviewInstalled
        ? 'Saved restore validation had no matching messages; showing cached restore'
        : 'Saved restore had no matching messages');
      return;
    }
    if (result.kind === 'installed' && this.isCurrentStart(token)) {
      this.preserveMountedWindowForFreshness = true;
      try {
        await this.ensureFresh('start');
      } finally {
        this.preserveMountedWindowForFreshness = false;
      }
    }
  }

  private async showCachedAnchor(savedRestore: FeedAnchorRestoreRecord, token: number): Promise<boolean> {
    try {
      const cached = await this.buffer.readAround(
        this.accountId,
        this.conversationId,
        { id: savedRestore.id, sortOrder: savedRestore.sortOrder },
        this.pageSize
      );
      this.updateBufferHealth();
      if (!this.isCurrentStart(token) || cached.kind !== 'hit' || cached.page.messages.length === 0) return false;

      const page = cached.page;
      const projectedMessages = this.projectPage(page);
      const restoreTarget = resolveRestoreTarget(projectedMessages, page, savedRestore);
      if (!restoreTarget) return false;

      const edges: EdgeState = {
        top: page.olderCursor ? 'idle' : 'end',
        // A warm cached anchor that reaches the live tail is presumed contiguous while
        // validation is in flight, matching the cached-latest edge behavior.
        bottom: page.newerCursor ? 'idle' : 'live'
      };
      this.controller.replaceWindow(
        projectedMessages,
        { kind: 'item', key: restoreTarget.key, align: 'start', offsetPx: restoreTarget.offsetPx },
        {
          olderCursor: page.olderCursor,
          newerCursor: page.newerCursor,
          syncCursor: page.syncCursor
        },
        edges
      );
      this.restorePersistenceReady = true;
      this.recordBootFirstWindow('cache-restore', projectedMessages.length);
      this.setStatus(`Engine cached restore: ${projectedMessages.length} messages`);
      return true;
    } catch (error: unknown) {
      this.reportBufferError(error);
      return false;
    }
  }

  private async showCachedLatest(token: number): Promise<number | undefined> {
    try {
      const cached = await this.buffer.readLatest(this.accountId, this.conversationId, this.pageSize);
      this.updateBufferHealth();
      if (!this.isCurrentStart(token) || cached.messages.length === 0) return undefined;

      const projectedMessages = this.projectPage(cached);
      const olderCursor = cached.messages[0]?.sortOrder;
      const edges: EdgeState = {
        top: olderCursor ? 'idle' : 'end',
        // A warm cached-latest window is presumed live-tail-contiguous while background
        // validation confirms it: 'live' keeps edge-zone height at zero (no phantom gap),
        // hides Jump-to-Present, and lets interim sends/creates behave normally. A stale
        // cached tail is corrected by the validated install; failure flips to 'error'.
        bottom: 'live'
      };
      this.controller.replaceWindow(
        projectedMessages,
        { kind: 'bottom' },
        {
          olderCursor,
          newerCursor: undefined,
          syncCursor: undefined
        },
        edges
      );
      const cachedEpoch = this.controller.snapshot().windowEpoch;
      this.restorePersistenceReady = true;
      this.setBootLatestValidationState({ kind: 'loading' });
      this.recordBootFirstWindow('cache-latest', projectedMessages.length);
      this.setStatus(`Engine cached window: ${projectedMessages.length} messages`);
      return cachedEpoch;
    } catch (error: unknown) {
      this.reportBufferError(error);
      return undefined;
    }
  }

  private async bootLatestWindow(token: number): Promise<void> {
    this.setBootLatestValidationState(IDLE_BOOT_LATEST_VALIDATION_STATE);
    const cachedEpoch = await this.showCachedLatest(token);
    if (!this.isCurrentStart(token)) return;
    this.bootLatestCachedEpoch = cachedEpoch;
    this.setBootLatestValidationState({ kind: 'loading' });
    await this.ensureFresh('start');
    if (
      !this.isCurrentStart(token) ||
      this.snapshot.freshnessError ||
      this.freshnessLastSucceededAt === undefined
    ) return;
    this.restorePersistenceReady = true;
    this.recordBootFirstWindow('boot', this.controller.snapshot().items.length);
    this.setStatus(`Engine static window: ${this.controller.snapshot().items.length} messages from ${this.sourceLabel()}`);
  }

  private handleBootLatestValidationFailure(cachedEpoch: number | undefined, error: unknown): void {
    if (cachedEpoch === undefined) return;

    const message = errorMessage(error);
    if (!this.controller.setEdgesIfCurrent(cachedEpoch, { bottom: 'error' })) {
      this.setBootLatestValidationState(IDLE_BOOT_LATEST_VALIDATION_STATE);
      return;
    }

    this.setBootLatestValidationState({ kind: 'error', message });
    this.setStatus(`Engine latest validation failed: ${message}`);
  }

  private async installFetchedWindow(input: InstallFetchedWindowInput): Promise<InstallFetchedWindowResult> {
    const membershipGeneration = this.buffer.membershipGeneration(this.accountId);
    const result = await this.controller.guardedPageFetch({
      direction: input.direction,
      fetchPage: input.fetch,
      limit: input.limit ?? this.pageSize,
      pageDebug,
      reason: input.reason,
      requestDebug: input.requestDebug,
      resetWindow: input.resetWindow
    });
    if (input.isCurrent && !input.isCurrent()) return { kind: 'stale' };
    if (result.kind === 'error') return { kind: 'error', error: result.error };
    if (result.kind === 'skipped') return { kind: 'skipped', reason: result.reason };
    if (result.kind === 'dropped') return { kind: 'dropped', reason: result.reason };

    const mergeResult = await this.mergePage(input.mergeRequest, result.page, membershipGeneration);
    if (!mergeResult) return { kind: 'merge-failed', page: result.page };
    if (input.isCurrent && !input.isCurrent()) return { kind: 'stale', page: result.page };
    if (mergeResult.revoked) {
      this.handleRevokedMerge({ direction: input.direction, reason: input.reason });
      return { kind: 'revoked', page: result.page };
    }

    const projectedMessages = this.projectPage(result.page);
    const location = input.location(result.page, projectedMessages);
    if (!location) {
      this.trimActiveWireToLoadedItems();
      return { kind: 'missing-location', page: result.page, projectedMessages };
    }

    const installed = this.controller.replaceWindowIfCurrent(
      result.epoch,
      projectedMessages,
      location,
      input.cursors(result.page),
      input.edges?.(result.page)
    );
    if (!installed) {
      this.trimActiveWireToLoadedItems();
      return { kind: 'not-installed', page: result.page, projectedMessages };
    }

    const replayedEventCount = await this.replayRecentMessageEventsAfterSnapshot(result.page.syncCursor, {
      debugKind: `window:${input.reason}:replay`,
      isCurrent: input.isCurrent
    });
    this.trimActiveWireToLoadedItems();
    const installedResult = {
      mergeResult,
      page: result.page,
      projectedMessages,
      replayedEventCount
    };
    await input.onInstalled?.(installedResult);
    this.recordDebug('window:install', {
      reason: input.reason,
      replayed: replayedEventCount,
      count: projectedMessages.length
    });
    return { kind: 'installed', ...installedResult };
  }

  private async replaceWithLatestWindow(): Promise<boolean> {
    const result = await this.reconcileLatestSnapshot({
      display: 'install-at-bottom',
      reason: 'replace-with-latest',
      resetWindow: true
    });
    if (result.kind === 'error') throw result.error;
    if (result.kind === 'merge-failed') throw new Error('latest page could not be cached');
    return result.kind === 'success';
  }

  private async reconcileLatestSnapshot(input: {
    readonly display: LatestDisplayPolicy;
    readonly forcePreserveMountedWindow?: boolean;
    readonly isCurrent?: () => boolean;
    readonly representedClientRequestIds?: ReadonlySet<ClientRequestId>;
    readonly representedIds?: ReadonlySet<MessageId>;
    readonly representedNewestSortOrder?: SortOrder;
    readonly representedUnseenPresentCount?: number;
    readonly reason: string;
    readonly resetWindow?: boolean;
  }): Promise<ReconcileLatestSnapshotResult> {
    const before = this.controller.snapshot();
    const representedIds = input.representedIds ?? new Set(before.items.flatMap((item) => item.id ? [item.id] : []));
    const representedClientRequestIds = input.representedClientRequestIds ?? new Set(before.items.flatMap((item) =>
      item.clientRequestId ? [item.clientRequestId] : []
    ));
    const representedNewestSortOrder = input.representedNewestSortOrder ?? newestSortOrder(before.items);
    const representedUnseenPresentCount =
      input.representedUnseenPresentCount ?? this.snapshot.unseenPresentCount;
    const recoveringBootLatestError =
      this.snapshot.bootLatestValidationState.kind === 'error' && !before.newerCursor;
    const wasHistoric = !recoveringBootLatestError && this.controller.isHistoricWindow();
    // forcePreserveMountedWindow only suppresses the install branch: right after a
    // restore install the engine can report atBottom before scroll settles. A
    // tail-reaching restore must still fall through to the anchor-preserving merge
    // so rows created between the restore fetch and this snapshot are displayed.
    const wasAtLiveBottom = !wasHistoric &&
      input.forcePreserveMountedWindow !== true &&
      this.engine.getScrollState().atBottom;
    const membershipGeneration = this.buffer.membershipGeneration(this.accountId);
    const fetched = await this.controller.guardedPageFetch({
      direction: 'latest',
      fetchPage: () => this.server.latest(this.pageSize),
      limit: this.pageSize,
      pageDebug,
      reason: input.reason,
      resetWindow: input.resetWindow
    });
    if (input.isCurrent && !input.isCurrent()) return { kind: 'stale' };
    if (fetched.kind === 'error') return { kind: 'error', error: fetched.error };
    if (fetched.kind !== 'accepted') return { kind: 'stale' };

    const mergeResult = await this.mergePage({ kind: 'latest' }, fetched.page, membershipGeneration);
    if (!mergeResult) return { kind: 'merge-failed' };
    if (input.isCurrent && !input.isCurrent()) return { kind: 'stale' };
    if (mergeResult.revoked) {
      this.handleRevokedMerge({ direction: 'latest', reason: input.reason });
      return { kind: 'revoked' };
    }

    const projectedMessages = this.projectPage(fetched.page);
    const currentIds = new Set(before.items.flatMap((item) => item.id ? [item.id] : []));
    const overlaps = projectedMessages.some((item) => item.id && currentIds.has(item.id));
    let displayOutcome: LatestDisplayOutcome;
    if (input.display === 'install-at-bottom' || wasAtLiveBottom) {
      if (!this.controller.replaceLatestWindowIfCurrent(fetched.epoch, projectedMessages, {
        olderCursor: fetched.page.olderCursor,
        newerCursor: undefined,
        syncCursor: fetched.page.syncCursor
      })) return { kind: 'stale' };
      this.setUnseenPresentCount(0);
      displayOutcome = 'installed';
    } else if (wasHistoric) {
      this.setUnseenPresentCount((count) => Math.max(1, count));
      displayOutcome = 'preserved';
    } else if (overlaps) {
      const merged = this.controller.mergeLatestWindowIfCurrent(
        fetched.epoch,
        projectedMessages,
        mergeResult.removedIds,
        fetched.page.syncCursor
      );
      if (!merged) return { kind: 'stale' };
      const newCount = uniqueNewServerRows(
        this.controller.snapshot().items,
        representedIds,
        representedClientRequestIds,
        representedNewestSortOrder
      );
      const targetCount = representedUnseenPresentCount + newCount;
      this.setUnseenPresentCount((count) => Math.max(count, targetCount));
      displayOutcome = 'preserved';
    } else {
      const lastLoadedSortOrder = [...before.items].reverse().find((item) => item.sortOrder)?.sortOrder;
      if (lastLoadedSortOrder) this.controller.detachLiveTail(lastLoadedSortOrder);
      this.setUnseenPresentCount((count) => Math.max(1, count));
      displayOutcome = 'detached';
    }

    const replayedEventCount = await this.replayRecentMessageEventsAfterSnapshot(fetched.page.syncCursor, {
      debugKind: `window:${input.reason}:replay`,
      isCurrent: input.isCurrent
    });
    this.trimActiveWireToLoadedItems();
    if (input.isCurrent && !input.isCurrent()) return { kind: 'stale' };
    this.setBootLatestValidationState(IDLE_BOOT_LATEST_VALIDATION_STATE);
    this.recordDebug('window:latest-reconcile', {
      reason: input.reason,
      display: input.display,
      outcome: displayOutcome,
      count: projectedMessages.length,
      replayed: replayedEventCount
    });
    return {
      kind: 'success',
      displayOutcome,
      mergeResult,
      page: fetched.page,
      projectedMessages,
      replayedEventCount
    };
  }

  private async replayRecentMessageEventsAfterSnapshot(
    snapshotCursor: SyncCursor | undefined,
    options: {
      readonly debugKind: string;
      readonly isCurrent?: () => boolean;
      readonly messageIds?: ReadonlySet<MessageId>;
      readonly sortOrders?: ReadonlySet<SortOrder>;
    }
  ): Promise<number> {
    const replayable = replayableRecentMessageEvents(
      this.recentMessageEvents,
      snapshotCursor,
      options.messageIds,
      options.sortOrders
    );
    this.recordDebug(options.debugKind, {
      buffered: this.recentMessageEvents.length,
      replayed: replayable.length,
      scoped: options.messageIds?.size
    });

    let replayed = 0;
    for (const { event } of replayable) {
      if (options.isCurrent && !options.isCurrent()) break;
      await this.handleSyncEvent(event);
      if (options.isCurrent && !options.isCurrent()) break;
      replayed += 1;
    }
    return replayed;
  }

  private async runFreshnessCycle(reason: FeedFreshnessReason): Promise<void> {
    const startedAt = this.freshnessNow();
    let retryIndex = 0;
    let attempt = 0;
    this.freshnessRetryReset = false;
    this.recordDebug('freshness:start', {
      accountId: this.accountId,
      conversationId: this.conversationId,
      reason,
      startedAt
    });

    while (this.running) {
      this.freshnessSnapshotIssued = false;
      attempt += 1;
      try {
        const completed = await this.reconcileFreshnessOnce(reason, attempt);
        if (!completed) return;
        // Each trailing pass consumes the flag; only a signal arriving after the
        // newest snapshot issuance re-queues one, so a signal that races the
        // trailing snapshot is never lost and trailing work stays signal-bounded.
        if (this.freshnessTrailing) {
          this.freshnessTrailing = false;
          retryIndex = 0;
          attempt = 0;
          continue;
        }

        this.freshnessLastSucceededAt = this.freshnessNow();
        this.freshnessTrailing = false;
        this.updateSnapshot({ freshnessError: undefined });
        this.recordDebug('freshness:complete', {
          attempt,
          completedAt: this.freshnessLastSucceededAt,
          reason,
          startedAt
        });
        return;
      } catch (error: unknown) {
        this.invalidateFreshness('failed-reconciliation');
        if (this.freshnessRetryReset) {
          retryIndex = 0;
          this.freshnessRetryReset = false;
        }
        if (
          retryIndex >= FEED_FRESHNESS_RETRY_CAPS_MS.length ||
          !this.running ||
          !this.isDocumentVisible() ||
          !this.isOnline()
        ) {
          const message = errorMessage(error);
          this.updateSnapshot({ freshnessError: message });
          if (reason === 'start') {
            if (this.bootLatestCachedEpoch === undefined) {
              this.setBootLatestValidationState({ kind: 'error', message });
            } else {
              this.handleBootLatestValidationFailure(this.bootLatestCachedEpoch, error);
            }
          }
          this.setStatus(`Engine freshness failed: ${message}`);
          this.recordDebug('freshness:failed', { attempt, reason, error: message, terminal: true });
          return;
        }

        const cap = FEED_FRESHNESS_RETRY_CAPS_MS[retryIndex]!;
        const delayMs = Math.floor(Math.max(0, Math.min(1, this.freshnessRandom())) * cap);
        retryIndex += 1;
        this.recordDebug('freshness:retry', { attempt, cap, delayMs, reason, retry: retryIndex });
        await this.waitForFreshnessRetry(delayMs);
        if (!this.running) return;
      }
    }
  }

  private async reconcileFreshnessOnce(reason: FeedFreshnessReason, attempt: number): Promise<boolean> {
    const token = this.startToken;
    const conversationId = this.conversationId;
    const before = this.controller.snapshot();
    const representedIds = new Set(before.items.flatMap((item) => item.id ? [item.id] : []));
    const representedClientRequestIds = new Set(before.items.flatMap((item) =>
      item.clientRequestId ? [item.clientRequestId] : []
    ));
    const representedNewestSortOrder = newestSortOrder(before.items);
    const representedUnseenPresentCount = this.snapshot.unseenPresentCount;
    const isCurrent = () => this.running && this.startToken === token && this.conversationId === conversationId;
    const cursor = await this.resyncCursor(isCurrent);
    if (!isCurrent()) return false;

    this.setStatus(`Engine reconciling ${reason}`);
    if (cursor !== undefined) {
      const result = await this.server.sync(cursor);
      if (!isCurrent()) return false;
      await this.applyServerSyncResult(cursor, result, isCurrent);
      if (!isCurrent()) return false;
      // A heartbeat is a probe, not evidence of a gap: an empty journal diff from
      // a durable cursor proves nothing changed server-side, so the latest
      // snapshot is skipped. Any event or overflow falls through to the full cycle.
      if (reason === 'heartbeat') {
        if (!result.overflow && result.events.length === 0) {
          this.recordDebug('freshness:heartbeat-quiet', { attempt, cursor });
          return true;
        }
        // Changes may already have arrived live; only sync verifies the interval.
        this.recordDebug('freshness:heartbeat-changes', {
          attempt,
          conversationId,
          cursor,
          eventCount: result.events.length,
          overflow: result.overflow
        });
      }
    } else {
      this.recordDebug('freshness:sync-skipped', { attempt, reason, noDurableCursor: true });
    }

    this.freshnessSnapshotIssued = true;
    const latest = await this.reconcileLatestSnapshot({
      display: 'preserve-reader',
      forcePreserveMountedWindow: this.preserveMountedWindowForFreshness,
      isCurrent,
      representedClientRequestIds,
      representedIds,
      representedNewestSortOrder,
      representedUnseenPresentCount,
      reason
    });
    if (!isCurrent()) return false;
    if (latest.kind !== 'success') {
      if (latest.kind === 'error') throw latest.error;
      if (latest.kind === 'merge-failed') throw new Error('latest page could not be cached');
      return false;
    }
    this.setStatus(`Engine freshness reconciled ${latest.projectedMessages.length} latest messages`);
    return true;
  }

  private async resyncCursor(isCurrent: () => boolean): Promise<SyncCursor | undefined> {
    try {
      const persisted = await this.buffer.readSyncCursor(this.accountId, this.conversationId);
      if (!isCurrent()) return undefined;
      this.updateBufferHealth();
      if (persisted) {
        return persisted;
      }
    } catch (error: unknown) {
      if (isCurrent()) this.reportBufferError(error);
    }

    return undefined;
  }

  private waitForFreshnessRetry(delayMs: number): Promise<void> {
    return new Promise((resolve) => {
      const finish = () => {
        this.freshnessRetryTimer = undefined;
        this.freshnessRetryResolve = undefined;
        resolve();
      };
      this.freshnessRetryResolve = finish;
      this.freshnessRetryTimer = this.freshnessScheduler.setTimeout(finish, delayMs);
    });
  }

  private cancelFreshnessRetry(): void {
    if (this.freshnessRetryTimer !== undefined) {
      this.freshnessScheduler.clearTimeout(this.freshnessRetryTimer);
      this.freshnessRetryTimer = undefined;
    }
    const resolve = this.freshnessRetryResolve;
    this.freshnessRetryResolve = undefined;
    resolve?.();
  }

  private findLoadedJumpTarget(input: FeedMessageJumpInput): ChatItem | undefined {
    return this.controller.snapshot().items.find((item) => {
      if (input.id !== undefined && item.id === input.id) return true;
      return item.sortOrder === input.sortOrder;
    });
  }

  private async handleSyncEvent(event: SyncEvent): Promise<void> {
    const membershipGeneration = this.buffer.membershipGeneration(this.accountId);
    if (event.type !== 'entity.updated' && event.message && event.message.channelId !== this.conversationId) {
      this.recordDebug('live:socket-inactive', syncEventDebug(event));
      await this.persistEvent(event, membershipGeneration, event.message.channelId);
      return;
    }

    rememberRecentMessageEvent(this.recentMessageEvents, event);

    const eventWorkspaceIds = workspaceIdsForEvent(
      event,
      this.loadedConversations,
      this.accountId,
      this.conversationId
    );
    this.recordDebug('live:socket', syncEventDebug(event));
    if (membershipGeneration < this.buffer.membershipGeneration(this.accountId)) return;
    const carriesEntities = event.type === 'entity.updated' || Boolean(event.entities);
    const previousEntityRevisions = carriesEntities
      ? entityRevisionsForWorkspaces(this.buffer, this.accountId, eventWorkspaceIds)
      : undefined;
    const persistThenFanOutEntities = (ingestChanges: () => EntityChangeSet | undefined) => {
      void this.persistEvent(event, membershipGeneration)
        .then((persisted) => {
          if (!persisted) {
            this.updateBufferHealth();
            return;
          }
          if (!previousEntityRevisions) return;
          if (membershipGeneration < this.buffer.membershipGeneration(this.accountId)) return;
          const advancedWorkspaceIds = workspacesWithAdvancedEntityRevision(
            this.buffer,
            this.accountId,
            eventWorkspaceIds,
            previousEntityRevisions
          );
          if (advancedWorkspaceIds.length === 0) return;
          const changes = ingestChanges();
          if (!changes || !hasEntityChanges(changes)) return;
          const changedCount = applyEntityChangesForWorkspaces(
            this.buffer,
            this.accountId,
            advancedWorkspaceIds,
            changes,
            (signal) => this.applyEntityChanges(signal)
          );
          if (changedCount > 0) this.setStatus(`Engine re-projected ${changedCount} rows for entity update`);
        })
        .catch((error: unknown) => {
          this.setStatus(`Engine live event failed: ${errorMessage(error)}`);
        });
    };

    if (event.type === 'entity.updated') {
      this.setStatus('Engine persisting entity update');
      persistThenFanOutEntities(() => this.entityStore.ingest(event.entities));
      return;
    }

    if (event.type === 'message.deleted' && event.hardDeleted) {
      // Display-then-persist like any live event: the row vanishes now (the
      // engine re-anchors per §7.2 if it was the anchor); the durable purge
      // and segment normalization ride the enqueued persist.
      const ids = event.messageId ? [event.messageId] : [];
      const removed = this.controller.removeLoadedItems(ids, [event.sortOrder]);
      if (event.messageId) this.activeWireById.delete(event.messageId);
      const identity = event.messageId ?? event.sortOrder;
      this.setStatus(
        removed
          ? `Engine hard-deleted ${identity}`
          : `Engine persisted hard delete ${identity}`
      );
      const hardDeleteBundle = event.entities ? this.entityStore.ingest(event.entities) : undefined;
      persistThenFanOutEntities(() => hardDeleteBundle);
      return;
    }

    if (event.message) {
      rememberWireMessages(this.activeWireById, [event.message]);
      rememberLoadedConversation(
        this.loadedConversations,
        this.accountId,
        this.conversationId,
        [event.message],
        this.activeWireById,
        this.controller,
        this.entityStore
      );
    }
    const bundleChanges = event.entities ? this.entityStore.ingest(event.entities) : undefined;
    const projectedMessage = event.message ? projectMessage(event.message, this.entityStore) : undefined;
    const displayLiveCreate = (projected: ChatItem, clientRequestId?: ClientRequestId) => {
      if (clientRequestId && this.controller.reconcileSendAck(clientRequestId, projected)) {
        this.recordDebug('send:ack-reconciled', { clientRequestId });
        this.setStatus(`Composer sent ${projected.id}`);
        return;
      }

      const wasAtBottom = this.engine.getScrollState().atBottom;
      const applied = this.controller.applyLiveCreate(projected, 'pin');
      if (applied.kind === 'historic') {
        this.setUnseenPresentCount((count) => count + 1);
        this.setStatus(`Engine has newer live message outside historic slice`);
        return;
      }
      if (!applied.inserted) return;

      if (!wasAtBottom) {
        this.setUnseenPresentCount((count) => count + 1);
      }
      this.setStatus(`Engine appended ${projected.author.name}`);
    };

    if (event.type === 'message.created' && projectedMessage) {
      displayLiveCreate(projectedMessage, event.clientRequestId);
    } else if ((event.type === 'message.updated' || event.type === 'message.deleted') && projectedMessage) {
      if (this.controller.applyLoadedUpdate(projectedMessage)) {
        this.setStatus(`Engine updated ${projectedMessage.author.name}`);
      }
    }
    this.trimActiveWireToLoadedItems();
    persistThenFanOutEntities(() => bundleChanges);
  }

  private async applySyncOverflow(
    cursor: SyncCursor,
    nextCursor: SyncCursor,
    isCurrent: () => boolean
  ): Promise<void> {
    const membershipGeneration = this.buffer.membershipGeneration(this.accountId);
    await this.buffer.markConversationStale(this.accountId, this.conversationId, membershipGeneration);
    if (!isCurrent()) return;
    this.updateBufferHealth();
    if (!this.buffer.isHealthy()) throw new Error('sync overflow cache could not be stale-marked');
    if (!(await this.persistSyncCursor(nextCursor, membershipGeneration))) {
      throw new Error('sync overflow cursor could not be persisted');
    }
    if (!isCurrent()) return;

    if (membershipGeneration < this.buffer.membershipGeneration(this.accountId)) {
      this.handleRevokedMerge({ direction: 'sync', reason: 'overflow' });
      return;
    }

    const snapshot = this.controller.snapshot();
    const atLiveBottom = !snapshot.newerCursor && this.engine.getScrollState().atBottom;
    this.recordDebug('sync:overflow', { atLiveBottom, cursor, nextCursor });
    if (atLiveBottom) return;

    const lastLoadedSortOrder = snapshot.items.at(-1)?.sortOrder;
    if (lastLoadedSortOrder) {
      this.controller.detachLiveTail(lastLoadedSortOrder);
    }
    this.setUnseenPresentCount((count) => Math.max(1, count));
    this.setStatus(
      lastLoadedSortOrder
        ? 'Engine sync overflow kept current window and detached live tail'
        : 'Engine sync overflow marked cached pages stale'
    );
  }

  private async applyResyncEvents(
    events: readonly SyncEvent[],
    nextCursor: SyncCursor,
    isCurrent: () => boolean
  ): Promise<ResyncApplyResult | undefined> {
    const membershipGeneration = this.buffer.membershipGeneration(this.accountId);
    const conversationId = this.conversationId;
    const deletes: MessageId[] = [];
    const deleteSortOrders: SortOrder[] = [];
    const updates: ChatItem[] = [];
    const creates: ChatItem[] = [];
    let ackedSends = 0;
    let entityFanoutCount = 0;

    for (const event of events) {
      const eventWorkspaceIds = workspaceIdsForEvent(
        event,
        this.loadedConversations,
        this.accountId,
        conversationId
      );
      const previousEntityRevisions = entityRevisionsForWorkspaces(this.buffer, this.accountId, eventWorkspaceIds);
      this.recordDebug('sync:resync-event', syncEventDebug(event));
      if (event.type !== 'entity.updated' && event.message && event.message.channelId !== conversationId) {
        if (!(await this.persistEvent(event, membershipGeneration, event.message.channelId))) {
          return undefined;
        }
        if (!isCurrent()) return undefined;
        continue;
      }

      if (!(await this.persistEvent(event, membershipGeneration, conversationId))) {
        return undefined;
      }
      if (!isCurrent()) return undefined;
      rememberRecentMessageEvent(this.recentMessageEvents, event);
      if (membershipGeneration < this.buffer.membershipGeneration(this.accountId)) {
        this.handleRevokedMerge({ direction: 'sync', reason: 'resync' });
        return undefined;
      }

      if (event.type === 'entity.updated') {
        const advancedWorkspaceIds = workspacesWithAdvancedEntityRevision(
          this.buffer,
          this.accountId,
          eventWorkspaceIds,
          previousEntityRevisions
        );
        if (advancedWorkspaceIds.length > 0) {
          entityFanoutCount += applyEntityChangesForWorkspaces(
            this.buffer,
            this.accountId,
            advancedWorkspaceIds,
            this.entityStore.ingest(event.entities),
            (signal) => this.applyEntityChanges(signal)
          );
        }
        continue;
      }

      if (event.message) {
        rememberWireMessages(this.activeWireById, [event.message]);
        rememberLoadedConversation(
          this.loadedConversations,
          this.accountId,
          conversationId,
          [event.message],
          this.activeWireById,
          this.controller,
          this.entityStore
        );
      }

      const advancedWorkspaceIds = event.entities
        ? workspacesWithAdvancedEntityRevision(this.buffer, this.accountId, eventWorkspaceIds, previousEntityRevisions)
        : [];
      if (event.entities && advancedWorkspaceIds.length > 0) {
        entityFanoutCount += applyEntityChangesForWorkspaces(
          this.buffer,
          this.accountId,
          advancedWorkspaceIds,
          this.entityStore.ingest(event.entities),
          (signal) => this.applyEntityChanges(signal)
        );
      }

      const projectedMessage = event.message ? projectMessage(event.message, this.entityStore) : undefined;
      if (event.type === 'message.created' && projectedMessage) {
        if (event.clientRequestId && this.controller.reconcileSendAck(event.clientRequestId, projectedMessage)) {
          this.recordDebug('send:ack-reconciled', { clientRequestId: event.clientRequestId });
          ackedSends += 1;
        } else {
          creates.push(projectedMessage);
        }
        continue;
      }

      if (event.type === 'message.updated' && projectedMessage) {
        updates.push(projectedMessage);
        continue;
      }

      if (event.type === 'message.deleted') {
        if (event.hardDeleted) {
          if (event.messageId) deletes.push(event.messageId);
          deleteSortOrders.push(event.sortOrder);
        } else if (projectedMessage) {
          updates.push(projectedMessage);
        }
      }
    }

    if (!(await this.persistSyncCursor(nextCursor, membershipGeneration))) {
      return undefined;
    }
    if (!isCurrent()) return undefined;
    const diffResult = this.controller.applyResyncDiff({ deletes, deleteSortOrders, updates, creates }, 'pin');
    this.trimActiveWireToLoadedItems();
    return {
      ackedSends,
      diff: diffResult,
      entityFanoutCount,
      eventCount: events.length
    };
  }

  private applyEntityChanges(signal: WorkspaceEntityFanoutSignal): number {
    if (!hasEntityChanges(signal.changes)) return 0;

    let affectedCount = 0;
    for (const loaded of this.loadedConversations.values()) {
      if (loaded.accountId !== signal.accountId || !loaded.workspaceIds.has(signal.workspaceId)) {
        continue;
      }

      const loadedIds = new Set(
        loaded.controller.snapshot().items.flatMap((item) => (item.id === undefined ? [] : [item.id]))
      );
      const affectedItems: ChatItem[] = [];
      for (const wire of loaded.wireById.values()) {
        if (loadedIds.has(wire.id) && wireReferencesEntityChanges(wire, signal.changes)) {
          affectedItems.push(projectMessage(wire, loaded.entityStore));
        }
      }

      if (affectedItems.length > 0) {
        loaded.controller.applyEntityFanout(affectedItems);
        affectedCount += affectedItems.length;
        this.recordDebug('entity:fanout', {
          accountId: signal.accountId,
          workspaceId: signal.workspaceId,
          conversationId: loaded.conversationId,
          entityRevision: signal.entityRevision,
          count: affectedItems.length
        });
      }
    }

    return affectedCount;
  }

  private projectPage(page: BufferWindow): ChatItem[] {
    rememberWireMessages(this.activeWireById, page.messages);
    rememberLoadedConversation(
      this.loadedConversations,
      this.accountId,
      this.conversationId,
      page.messages,
      this.activeWireById,
      this.controller,
      this.entityStore
    );
    const changes = this.entityStore.ingest(page.entities);
    for (const workspaceId of workspaceIdsForMessages(page.messages)) {
      this.applyEntityChanges({
        accountId: this.accountId,
        workspaceId,
        entityRevision: this.buffer.entityRevision(this.accountId, workspaceId),
        changes
      });
    }
    return projectMessages(page.messages, this.entityStore);
  }

  private trimActiveWireToLoadedItems(): void {
    const loadedIds = new Set(this.controller.snapshot().items.flatMap((item) => item.id ? [item.id] : []));
    this.trimActiveWireToLoadedIds(loadedIds);
  }

  private trimActiveWireToLoadedIds(loadedIds: ReadonlySet<MessageId>): void {
    for (const id of this.activeWireById.keys()) {
      if (!loadedIds.has(id)) this.activeWireById.delete(id);
    }
  }

  private async mergePage(
    request: FeedPageMergeRequest,
    page: FeedPage,
    membershipGeneration: number
  ): Promise<FeedPageMergeResult | undefined> {
    return this.buffer.mergePage(this.accountId, this.conversationId, request, page, membershipGeneration)
      .then((result) => {
        this.updateBufferHealth();
        return result;
      })
      .catch((error: unknown) => {
        this.reportBufferError(error);
        return undefined;
      });
  }

  private async persistEvent(
    event: SyncEvent,
    membershipGeneration: number,
    conversationId = this.conversationId
  ): Promise<boolean> {
    return this.buffer.persistEvent(this.accountId, conversationId, event, membershipGeneration)
      .then(() => {
        this.updateBufferHealth();
        return this.buffer.isHealthy();
      })
      .catch((error: unknown) => {
        this.reportBufferError(error);
        return false;
      });
  }

  private async persistSyncCursor(cursor: SyncCursor, membershipGeneration: number): Promise<boolean> {
    return this.buffer.persistSyncCursor(this.accountId, this.conversationId, cursor, membershipGeneration)
      .then(() => {
        this.updateBufferHealth();
        return this.buffer.isHealthy();
      })
      .catch((error: unknown) => {
        this.reportBufferError(error);
        return false;
      });
  }

  private clearSignedOutState(message = 'Engine signed out'): void {
    this.invalidateFreshness('signed-out');
    this.cancelFreshnessRetry();
    this.freshnessTrailing = false;
    this.freshnessRetryReset = false;
    this.activeWireById.clear();
    this.loadedConversations.clear();
    this.recentMessageEvents = [];
    this.restorePersistenceReady = false;
    this.setBootLatestValidationState(IDLE_BOOT_LATEST_VALIDATION_STATE);
    this.setReturnToPresentState(IDLE_RETURN_TO_PRESENT_STATE);
    this.setUnseenPresentCount(0);
    this.updateSnapshot({ conversationId: this.conversationId });
    this.controller.replaceWindow([], { kind: 'bottom' }, {});
    this.setStatus(message);
  }

  private handleRevokedMerge(data?: Record<string, unknown>): void {
    this.recordDebug('buffer:merge-page-revoked', data);
    this.clearSignedOutState();
  }

  private viewerAuthor(): ChatAuthor {
    return this.authorForProfile(this.viewerProfileId);
  }

  private authorForProfile(profileId: ChatAuthor['id']): ChatAuthor {
    const profile = this.entityStore.getProfile(profileId);
    return {
      id: profileId,
      name: profile?.name ?? 'Ada',
      accent: profile?.accent ?? '#2563eb',
      ...(profile?.avatarUrl === undefined ? {} : { avatarUrl: profile.avatarUrl })
    };
  }

  private subscribeToServer(): void {
    this.unsubscribeSocket = this.server.subscribe((event) => {
      void this.handleSyncEvent(event).catch((error: unknown) => {
        this.setStatus(`Engine live event failed: ${errorMessage(error)}`);
      });
    });
    this.unsubscribeReactionDelta = this.server.onReactionDelta?.((delta) => {
      this.applyReactionDelta(delta.action, delta.reaction);
    });
    this.unsubscribeLocalOptimistic = this.server.onLocalOptimistic?.((delta) => {
      this.applyLocalOptimisticMessage(delta.message);
    });
  }

  private unsubscribeFromServer(): void {
    this.unsubscribeSocket?.();
    this.unsubscribeSocket = undefined;
    this.unsubscribeReactionDelta?.();
    this.unsubscribeReactionDelta = undefined;
    this.unsubscribeLocalOptimistic?.();
    this.unsubscribeLocalOptimistic = undefined;
  }

  private scheduleRestorePersistence(): void {
    this.clearRestoreTimer();
    this.restoreIdleTimer = setTimeout(() => {
      this.restoreIdleTimer = undefined;
      this.persistRestoreNow('idle');
    }, this.restoreScrollIdleDebounceMs);
  }

  private clearRestoreTimer(): void {
    if (this.restoreIdleTimer !== undefined) {
      clearTimeout(this.restoreIdleTimer);
      this.restoreIdleTimer = undefined;
    }
  }

  private isCurrentStart(token: number): boolean {
    return this.running && this.startToken === token;
  }

  private sourceLabel(): string {
    return this.server.sourceLabel ?? 'feed source';
  }

  private setReturnToPresentState(returnToPresentState: ReturnToPresentState): void {
    this.updateSnapshot({ returnToPresentState });
  }

  private setBootLatestValidationState(bootLatestValidationState: BootLatestValidationState): void {
    this.updateSnapshot({ bootLatestValidationState });
  }

  private setUnseenPresentCount(next: number | ((current: number) => number)): void {
    const unseenPresentCount = typeof next === 'function' ? next(this.snapshot.unseenPresentCount) : next;
    this.updateSnapshot({ unseenPresentCount });
  }

  private updateSnapshot(patch: Partial<FeedSessionSnapshot>): void {
    this.snapshot = {
      ...this.snapshot,
      ...patch
    };
    for (const subscriber of this.subscribers) {
      subscriber(this.snapshot);
    }
  }

  private recordDebug(kind: string, data?: Record<string, unknown>): void {
    this.callbacks.onDebugEvent?.(kind, data);
  }

  private beginBootPerformanceCycle(mode: 'latest' | 'restore' | 'jump'): void {
    this.bootFirstWindowMarked = false;
    this.recordDebug('performance:boot:start', {
      conversationId: this.conversationId,
      mode
    });
  }

  private recordBootFirstWindow(reason: string, count: number): void {
    if (this.bootFirstWindowMarked || count === 0) return;

    this.bootFirstWindowMarked = true;
    this.recordDebug('performance:boot:first-window', {
      count,
      reason
    });
  }

  private recordNoProgress(
    direction: 'older' | 'newer',
    source: 'cache' | 'cache-sync',
    cursor: SortOrder,
    page: FeedPage
  ): void {
    this.recordDebug('pagination:no-progress', {
      direction,
      source,
      cursor,
      count: page.messages.length,
      olderCursor: page.olderCursor,
      newerCursor: page.newerCursor
    });
  }

  private acceptsProgressiveCachePage(
    direction: 'older' | 'newer',
    source: 'cache' | 'cache-sync',
    cursor: SortOrder,
    page: FeedPage
  ): boolean {
    // Belt for future cache bugs only: cached pages must either end pagination or
    // move the cursor outward. Engine flush re-entrancy is handled by load latches
    // plus controller snapshot invalidation at mutation time.
    if (cachePageAdvancesPagination(direction, cursor, page)) return true;

    this.recordNoProgress(direction, source, cursor, page);
    return false;
  }

  private reportBufferError(error: unknown): void {
    this.callbacks.onBufferError?.(error);
    if (!this.callbacks.onBufferError) {
      this.recordDebug('buffer:error', { message: errorMessage(error) });
    }
    this.updateBufferHealth();
  }
}

function rememberWireMessages(wireById: Map<MessageId, WireMessage>, messages: readonly WireMessage[]): void {
  for (const message of messages) {
    wireById.set(message.id, message);
  }
}

function rememberLoadedConversation(
  loadedConversations: Map<string, LoadedConversation>,
  accountId: string,
  conversationId: ChannelId,
  messages: readonly WireMessage[],
  wireById: Map<MessageId, WireMessage>,
  controller: FeedController,
  entityStore: EntityStore
): void {
  const workspaceIds = workspaceIdsForMessages(messages);
  if (workspaceIds.length === 0) return;

  const key = loadedConversationKey(accountId, conversationId);
  const loaded = loadedConversations.get(key);
  if (loaded) {
    for (const workspaceId of workspaceIds) {
      loaded.workspaceIds.add(workspaceId);
    }
    return;
  }

  loadedConversations.set(key, {
    accountId,
    conversationId,
    controller,
    entityStore,
    wireById,
    workspaceIds: new Set(workspaceIds)
  });
}

function workspaceIdsForMessages(messages: readonly WireMessage[]): WorkspaceId[] {
  return [...new Set(messages.map((message) => message.workspaceId))];
}

function workspaceIdsForEvent(
  event: SyncEvent,
  loadedConversations: Map<string, LoadedConversation>,
  accountId: string,
  conversationId: ChannelId
): WorkspaceId[] {
  if (event.type !== 'entity.updated' && event.message) {
    return [event.message.workspaceId];
  }

  return [
    ...(loadedConversations.get(loadedConversationKey(accountId, conversationId))?.workspaceIds ?? [])
  ];
}

function entityRevisionsForWorkspaces(
  buffer: FeedBufferLike,
  accountId: string,
  workspaceIds: readonly WorkspaceId[]
): Map<WorkspaceId, number> {
  return new Map(workspaceIds.map((workspaceId) => [
    workspaceId,
    buffer.entityRevision(accountId, workspaceId)
  ]));
}

function workspacesWithAdvancedEntityRevision(
  buffer: FeedBufferLike,
  accountId: string,
  workspaceIds: readonly WorkspaceId[],
  previousRevisions: ReadonlyMap<WorkspaceId, number>
): WorkspaceId[] {
  return workspaceIds.filter((workspaceId) =>
    buffer.entityRevision(accountId, workspaceId) > (previousRevisions.get(workspaceId) ?? 0)
  );
}

function applyEntityChangesForWorkspaces(
  buffer: FeedBufferLike,
  accountId: string,
  workspaceIds: readonly WorkspaceId[],
  changes: EntityChangeSet,
  applyEntityChanges: (signal: WorkspaceEntityFanoutSignal) => number
): number {
  return workspaceIds.reduce(
    (count, workspaceId) => count + applyEntityChanges({
      accountId,
      workspaceId,
      entityRevision: buffer.entityRevision(accountId, workspaceId),
      changes
    }),
    0
  );
}

function loadedWorkspaceIds(
  loadedConversations: ReadonlyMap<string, LoadedConversation>,
  accountId: string
): WorkspaceId[] {
  const workspaceIds = new Set<WorkspaceId>();
  for (const loaded of loadedConversations.values()) {
    if (loaded.accountId !== accountId) continue;
    for (const workspaceId of loaded.workspaceIds) {
      workspaceIds.add(workspaceId);
    }
  }
  return [...workspaceIds];
}

function loadedConversationKey(accountId: string, conversationId: ChannelId): string {
  return `${accountId}:${conversationId}`;
}

function wireReferencesEntityChanges(wire: WireMessage, changes: EntityChangeSet): boolean {
  if (changes.profiles.has(wire.authorId)) return true;
  if (changes.channels.has(wire.channelId)) return true;
  return wire.reactions.some((reaction) => changes.emoji.has(reaction.emojiId));
}

function insertedMessageIds(
  incoming: readonly ChatItem[],
  current: readonly ChatItem[]
): ReadonlySet<MessageId> {
  const currentIds = new Set(current.flatMap((item) => (item.id === undefined ? [] : [item.id])));
  return new Set(incoming.flatMap((item) => (
    item.id === undefined || currentIds.has(item.id) ? [] : [item.id]
  )));
}

function sortOrdersForMessageIds(
  items: readonly ChatItem[],
  ids: ReadonlySet<MessageId>
): ReadonlySet<SortOrder> {
  return new Set(items.flatMap((item) => item.id && item.sortOrder && ids.has(item.id) ? [item.sortOrder] : []));
}

function cachePageAdvancesPagination(
  direction: 'older' | 'newer',
  cursor: SortOrder,
  page: FeedPage
): boolean {
  const nextCursor = direction === 'older' ? page.olderCursor : page.newerCursor;
  if (!nextCursor) return true;

  const comparison = compareSortOrders(nextCursor, cursor);
  return direction === 'older' ? comparison < 0 : comparison > 0;
}

function syncEventDebug(event: SyncEvent): Record<string, unknown> {
  if (event.type === 'entity.updated') {
    return {
      type: event.type,
      profiles: event.entities.profiles.map((profile) => profile.id),
      channels: event.entities.channels.map((channel) => channel.id),
      emoji: event.entities.emoji.map((emoji) => emoji.id)
    };
  }

  return {
    type: event.type,
    id: event.messageId,
    sortOrder: event.type === 'message.deleted' ? event.sortOrder : event.message.sortOrder
  };
}

function uniqueNewServerRows(
  items: readonly ChatItem[],
  representedIds: ReadonlySet<MessageId>,
  representedClientRequestIds: ReadonlySet<ClientRequestId>,
  representedNewestSortOrder: SortOrder | undefined
): number {
  const unseenIds = new Set<MessageId>();
  for (const item of items) {
    if (!item.id || representedIds.has(item.id)) continue;
    if (item.clientRequestId && representedClientRequestIds.has(item.clientRequestId)) continue;
    // Older rows paged in mid-reconciliation are pagination, not unseen present
    // content; only rows past the pre-reconciliation tail count.
    if (
      representedNewestSortOrder !== undefined &&
      item.sortOrder !== undefined &&
      compareSortOrders(item.sortOrder, representedNewestSortOrder) <= 0
    ) continue;
    unseenIds.add(item.id);
  }
  return unseenIds.size;
}

function newestSortOrder(items: readonly ChatItem[]): SortOrder | undefined {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    const sortOrder = items[index]?.sortOrder;
    if (sortOrder !== undefined) return sortOrder;
  }
  return undefined;
}

function emptyFeedPage(): FeedPage {
  return {
    messages: [],
    entities: {
      profiles: [],
      channels: [],
      emoji: []
    },
    syncCursor: ''
  };
}

function pageDebug(page: FeedPage): Record<string, unknown> {
  return {
    newerCursor: compactId(page.newerCursor),
    olderCursor: compactId(page.olderCursor),
    range: {
      count: page.messages.length,
      firstId: page.messages[0]?.id,
      lastId: page.messages.at(-1)?.id
    },
    syncCursor: page.syncCursor
  };
}

function compactId(id: string | undefined): string | undefined {
  return id ?? undefined;
}

function errorMessage(cause: unknown): string {
  if (cause instanceof Error) return cause.message;
  if (typeof cause === 'string' && cause.length > 0) return cause;
  return 'unhandled failure';
}
